import { AmmoType, WeaponCategory, } from '../types/weapons.js';
import { ResourceTier, RaceId } from '../types/entities.js';
/**
 * All weapons available in the game. The `pulse_blaster` is the default
 * fallback: every player keeps it unlocked even if their active weapon breaks
 * or runs out of ammo. Other entries are gated by player level, purchased at
 * stations, or assembled at the crafting bench.
 *
 * ── Damage / fire-rate balance principle ─────────────────────────────────
 *
 * `damage × fireRate` (× `projectileCount` for spread weapons) is the
 * weapon's *nominal* DPS. The actual DPS a pilot lands is meaningfully
 * lower than nominal because slow-cycling weapons miss whole shots when
 * the target dodges, while high-rate weapons saturate the firing arc
 * and land most pellets. To keep every weapon competitive we tune so:
 *
 *   1. Within a tier, "heavy" weapons (slow ROF, big per-shot damage)
 *      have ≥1.5× the nominal DPS of "chip" weapons (fast ROF, small
 *      per-shot damage). The ROF gap absorbs into hit-rate variance,
 *      and the heavy still wins in practice.
 *   2. Each tier's heaviest option clearly out-DPSes the previous
 *      tier's heaviest option. A pilot upgrading to a higher-tier
 *      heavy weapon should *feel* more lethal, not just see a bigger
 *      number on paper.
 *   3. Slow projectiles (speed < 350) are reserved for short-range or
 *      AoE weapons. Sniper-class heavies use speed ≥ 500 so the shot
 *      lands before the target turns away.
 *
 * The `needle_repeater` is the canonical chip weapon: highest nominal
 * ROF in tier 1, *lowest* nominal DPS in tier 1. It exists to pressure
 * shields, not to out-burst a coil_driver / chrono_lance.
 */
export const WEAPONS = {
    // ─── Tier 0 — Always unlocked fallback ───
    pulse_blaster: {
        id: 'pulse_blaster', name: 'Pulse Blaster', nameEs: 'Bláster de Pulso',
        tier: 0, damage: 5, fireRate: 4, range: 200, speed: 300, energyCost: 0,
        ammoType: AmmoType.Unlimited, maxAmmo: -1,
        projectileColor: 0xff4400, projectileSize: 0.15,
        category: WeaponCategory.Energy, craftCost: [], creditCost: 0, requiredLevel: 1,
        description: 'Reliable default blaster — unlimited shots, modest damage.',
        descriptionEs: 'Bláster básico fiable — disparos ilimitados y daño modesto.',
        visualStyle: 'blaster', muzzleColor: 0xff6622, trailStyle: 'basic',
    },
    // ─── Tier 1 — Early game ───
    mining_laser: {
        id: 'mining_laser', name: 'Mining Laser', nameEs: 'Láser Minero',
        tier: 1, damage: 14, fireRate: 7, range: 260, speed: 999, energyCost: 2,
        ammoType: AmmoType.Energy, maxAmmo: 250,
        projectileColor: 0xff9900, projectileSize: 0.28,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.ScrapMetal, amount: 20 }],
        creditCost: 500, requiredLevel: 2,
        description: 'Focused amber beam tuned to crack ore; doubles as a sidearm noticeably sharper than a pulse blaster.',
        descriptionEs: 'Rayo ámbar focalizado para romper mineral; arma auxiliar más potente que el bláster de pulso.',
        visualStyle: 'laser', muzzleColor: 0xffcc44, trailStyle: 'sparkle',
    },
    gatling_cannon: {
        id: 'gatling_cannon', name: 'Gatling Cannon', nameEs: 'Cañón Gatling',
        tier: 1, damage: 6, fireRate: 11, range: 240, speed: 520, energyCost: 1,
        ammoType: AmmoType.Kinetic, maxAmmo: 600,
        projectileColor: 0xffff44, projectileSize: 0.15,
        category: WeaponCategory.Projectile,
        craftCost: [
            { resource: ResourceTier.ScrapMetal, amount: 40 },
            { resource: ResourceTier.IronOre, amount: 20 },
        ],
        creditCost: 1200, requiredLevel: 4,
        description: 'Spun-up rotary cannon spraying tracers; excellent for whittling shields.',
        descriptionEs: 'Rotativa de cadencia alta, ideal para desgastar escudos.',
        visualStyle: 'gatling', muzzleColor: 0xffdd22, trailStyle: 'smoke',
    },
    needle_repeater: {
        // Canonical "chip weapon": fastest cycle in tier 1, *intentionally*
        // the lowest nominal DPS. Real DPS-on-target is competitive only
        // against weak-shielded targets where saturating the firing arc
        // actually pays off; against a heavy hull it gets out-killed by
        // mining_laser, gatling_cannon, or any tier ≥ 2 primary. Do NOT
        // raise damage past 3 or fireRate past 12 without rechecking the
        // tier-1 DPS hierarchy in `constants.test.ts`.
        id: 'needle_repeater', name: 'Needle Repeater', nameEs: 'Repetidor de Agujas',
        tier: 1, damage: 3, fireRate: 12, range: 180, speed: 480, energyCost: 0.5,
        ammoType: AmmoType.Kinetic, maxAmmo: 900,
        projectileColor: 0xcceeff, projectileSize: 0.1,
        category: WeaponCategory.Projectile,
        craftCost: [{ resource: ResourceTier.Silicate, amount: 20 }],
        creditCost: 1600, requiredLevel: 5,
        description: 'Slivers of silicate needled out at blistering speed.',
        descriptionEs: 'Astillas de silicato disparadas a velocidad fulgurante.',
        visualStyle: 'needler', muzzleColor: 0xccf0ff, trailStyle: 'sparkle',
    },
    light_missiles: {
        id: 'light_missiles', name: 'Light Missiles', nameEs: 'Misiles Ligeros',
        tier: 1, damage: 32, fireRate: 2.0, range: 400, speed: 320, energyCost: 5,
        ammoType: AmmoType.Missile, maxAmmo: 40,
        projectileColor: 0xff4444, projectileSize: 0.4,
        category: WeaponCategory.Missile,
        craftCost: [{ resource: ResourceTier.ScrapMetal, amount: 60 }],
        creditCost: 2000, requiredLevel: 6,
        description: 'Cheap dumbfire missiles — slow, loud, and hit like a hammer.',
        descriptionEs: 'Misiles baratos y no guiados — lentos pero devastadores.',
        visualStyle: 'missile', muzzleColor: 0xffaa66, trailStyle: 'smoke',
    },
    scatter_shotgun: {
        id: 'scatter_shotgun', name: 'Scatter Shotgun', nameEs: 'Escopeta Dispersora',
        tier: 1, damage: 14, fireRate: 2.2, range: 140, speed: 480, energyCost: 2,
        ammoType: AmmoType.Kinetic, maxAmmo: 200,
        projectileColor: 0xffcc66, projectileSize: 0.2,
        category: WeaponCategory.Projectile,
        craftCost: [{ resource: ResourceTier.CopperOre, amount: 25 }],
        creditCost: 2400, requiredLevel: 7, projectileCount: 5,
        description: 'Close-range cone of slugs. Each pellet rolls its own hit.',
        descriptionEs: 'Abanico de perdigones a corta distancia con impacto propio.',
        visualStyle: 'shotgun', muzzleColor: 0xffddaa, trailStyle: 'smoke',
    },
    // ─── Tier 2 — Mid game ───
    plasma_cannon: {
        id: 'plasma_cannon', name: 'Plasma Cannon', nameEs: 'Cañón de Plasma',
        tier: 2, damage: 28, fireRate: 3, range: 320, speed: 520, energyCost: 8,
        ammoType: AmmoType.Energy, maxAmmo: 200,
        projectileColor: 0xaa44ff, projectileSize: 0.5,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.CrystalShards, amount: 25 }],
        creditCost: 4500, requiredLevel: 10,
        description: 'Heavy violet plasma bolts that melt hulls on sustained hits.',
        descriptionEs: 'Pesados proyectiles de plasma violeta que funden cascos.',
        visualStyle: 'plasma', muzzleColor: 0xdd88ff, trailStyle: 'plasma',
    },
    ion_beam: {
        id: 'ion_beam', name: 'Ion Beam', nameEs: 'Rayo Iónico',
        tier: 2, damage: 22, fireRate: 5, range: 400, speed: 999, energyCost: 6,
        ammoType: AmmoType.Energy, maxAmmo: 220,
        projectileColor: 0x44ffff, projectileSize: 0.25,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.CrystalShards, amount: 30 }],
        creditCost: 5200, requiredLevel: 12,
        description: 'Continuous cyan beam — tears through shields, grazes hulls.',
        descriptionEs: 'Haz cian continuo — destroza escudos, araña cascos.',
        visualStyle: 'beam', muzzleColor: 0x88ffff, trailStyle: 'ion',
    },
    coil_driver: {
        id: 'coil_driver', name: 'Coil Driver', nameEs: 'Impulsor de Bobina',
        tier: 2, damage: 55, fireRate: 2.0, range: 440, speed: 800, energyCost: 10,
        ammoType: AmmoType.Kinetic, maxAmmo: 120,
        projectileColor: 0xaaddff, projectileSize: 0.35,
        category: WeaponCategory.Projectile,
        craftCost: [
            { resource: ResourceTier.TitaniumAlloy, amount: 10 },
            { resource: ResourceTier.CrystalShards, amount: 10 },
        ],
        creditCost: 6800, requiredLevel: 14,
        description: 'Electromagnetically-accelerated slugs punch straight through armor.',
        descriptionEs: 'Proyectiles acelerados electromagnéticamente, perforan blindaje.',
        visualStyle: 'coil', muzzleColor: 0xbbeeff, trailStyle: 'sparkle',
    },
    arc_welder: {
        id: 'arc_welder', name: 'Arc Welder', nameEs: 'Soldador de Arco',
        tier: 2, damage: 13, fireRate: 8, range: 220, speed: 999, energyCost: 4,
        ammoType: AmmoType.Energy, maxAmmo: 300,
        projectileColor: 0xffee88, projectileSize: 0.2,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.SolarPlasma, amount: 12 }],
        creditCost: 7500, requiredLevel: 16,
        description: 'Forked electric arcs that chain to nearby plating.',
        descriptionEs: 'Arcos eléctricos bifurcados que saltan entre placas.',
        visualStyle: 'arc', muzzleColor: 0xffff99, trailStyle: 'sparkle',
    },
    // ─── Tier 3 — High game ───
    railgun: {
        id: 'railgun', name: 'Railgun', nameEs: 'Cañón de Riel',
        tier: 3, damage: 140, fireRate: 0.85, range: 720, speed: 1100, energyCost: 20,
        ammoType: AmmoType.Kinetic, maxAmmo: 60,
        projectileColor: 0xffffff, projectileSize: 0.3,
        category: WeaponCategory.Projectile,
        craftCost: [
            { resource: ResourceTier.PlasmaCore, amount: 15 },
            { resource: ResourceTier.NeutroniumDust, amount: 8 },
        ],
        creditCost: 18000, requiredLevel: 25,
        description: 'High-velocity rail slug; slow to cycle, deadly on impact.',
        descriptionEs: 'Proyectil de riel a gran velocidad; lento pero letal.',
        visualStyle: 'rail', muzzleColor: 0xeeffff, trailStyle: 'plasma',
    },
    emp_torpedoes: {
        id: 'emp_torpedoes', name: 'EMP Torpedoes', nameEs: 'Torpedos EMP',
        tier: 3, damage: 75, fireRate: 1.0, range: 520, speed: 380, energyCost: 15,
        ammoType: AmmoType.Missile, maxAmmo: 30,
        projectileColor: 0x4488ff, projectileSize: 0.6,
        category: WeaponCategory.Missile,
        craftCost: [{ resource: ResourceTier.PlasmaCore, amount: 18 }],
        creditCost: 22000, requiredLevel: 28,
        description: 'Electromagnetic torpedoes that shred shields before touching armor.',
        descriptionEs: 'Torpedos EMP que destrozan escudos antes de tocar blindaje.',
        visualStyle: 'torpedo', muzzleColor: 0x88bbff, trailStyle: 'warp',
    },
    swarm_missiles: {
        id: 'swarm_missiles', name: 'Swarm Missiles', nameEs: 'Misiles de Enjambre',
        tier: 3, damage: 12, fireRate: 4, range: 400, speed: 380, energyCost: 6,
        ammoType: AmmoType.Missile, maxAmmo: 120,
        projectileColor: 0xff9966, projectileSize: 0.25,
        category: WeaponCategory.Missile,
        craftCost: [
            { resource: ResourceTier.PlasmaCore, amount: 10 },
            { resource: ResourceTier.Bioflux, amount: 8 },
        ],
        creditCost: 24000, requiredLevel: 30, projectileCount: 3,
        description: 'Small guided missiles fired in tight volleys.',
        descriptionEs: 'Pequeños misiles guiados lanzados en descargas apretadas.',
        visualStyle: 'swarm', muzzleColor: 0xffbb77, trailStyle: 'flame',
    },
    prism_splitter: {
        id: 'prism_splitter', name: 'Prism Splitter', nameEs: 'Divisor Prismático',
        tier: 3, damage: 24, fireRate: 3, range: 360, speed: 580, energyCost: 9,
        ammoType: AmmoType.Energy, maxAmmo: 150,
        projectileColor: 0xffaaee, projectileSize: 0.3,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.PhasePrism, amount: 4 }],
        creditCost: 30000, requiredLevel: 32, projectileCount: 3,
        description: 'Chromatic shards fan outward in a triple-spread pattern.',
        descriptionEs: 'Astillas cromáticas en abanico de triple dispersión.',
        visualStyle: 'prism', muzzleColor: 0xffccee, trailStyle: 'sparkle',
    },
    // ─── Tier 4 — End game ───
    disruptor_array: {
        id: 'disruptor_array', name: 'Disruptor Array', nameEs: 'Matriz Disruptora',
        tier: 4, damage: 44, fireRate: 4, range: 440, speed: 620, energyCost: 12,
        ammoType: AmmoType.Energy, maxAmmo: 160,
        projectileColor: 0xff2244, projectileSize: 0.4,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.VoidEssence, amount: 10 }],
        creditCost: 65000, requiredLevel: 40,
        description: 'Pulse array that bypasses a portion of hostile shields.',
        descriptionEs: 'Matriz de pulsos que atraviesa parte del escudo enemigo.',
        visualStyle: 'disruptor', muzzleColor: 0xff6688, trailStyle: 'void',
    },
    chrono_lance: {
        // Tier-4 sniper. Instant-hit beam (speed 999) so the only thing
        // gating its real DPS is fireRate. Tuned to ≥3× the needle_repeater's
        // nominal DPS — that gap exists specifically so that even with
        // realistic miss-rates a chrono pilot out-kills a needle pilot.
        id: 'chrono_lance', name: 'Chrono Lance', nameEs: 'Lanza Crónica',
        tier: 4, damage: 130, fireRate: 1.6, range: 620, speed: 999, energyCost: 22,
        ammoType: AmmoType.Special, maxAmmo: 80,
        projectileColor: 0xaaddee, projectileSize: 0.45,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.ChronoQuartz, amount: 5 }],
        creditCost: 120000, requiredLevel: 45,
        description: 'Time-slowed lance; ignores projectile drop and most armor.',
        descriptionEs: 'Lanza temporalmente ralentizada; ignora caída y blindaje.',
        visualStyle: 'chrono', muzzleColor: 0xbbf0ff, trailStyle: 'warp',
    },
    cluster_rockets: {
        id: 'cluster_rockets', name: 'Cluster Rockets', nameEs: 'Cohetes de Racimo',
        tier: 4, damage: 22, fireRate: 2.5, range: 460, speed: 380, energyCost: 10,
        ammoType: AmmoType.Missile, maxAmmo: 90,
        projectileColor: 0xffaa44, projectileSize: 0.35,
        category: WeaponCategory.Missile,
        craftCost: [
            { resource: ResourceTier.VoidEssence, amount: 8 },
            { resource: ResourceTier.NeutroniumDust, amount: 10 },
        ],
        creditCost: 150000, requiredLevel: 48, projectileCount: 4,
        description: 'Rockets that burst into a quartet of seeking submunitions.',
        descriptionEs: 'Cohetes que estallan en cuatro submuniciones rastreadoras.',
        visualStyle: 'cluster', muzzleColor: 0xffcc77, trailStyle: 'flame',
    },
    // ─── Tier 5 — Late game ───
    antimatter_howitzer: {
        id: 'antimatter_howitzer', name: 'Antimatter Howitzer', nameEs: 'Obús de Antimateria',
        tier: 5, damage: 200, fireRate: 0.9, range: 720, speed: 520, energyCost: 35,
        ammoType: AmmoType.Special, maxAmmo: 40,
        projectileColor: 0xff66ff, projectileSize: 0.7,
        category: WeaponCategory.Special,
        craftCost: [
            { resource: ResourceTier.DarkMatter, amount: 4 },
            { resource: ResourceTier.VoidPearl, amount: 3 },
        ],
        creditCost: 280000, requiredLevel: 55,
        description: 'Annihilation shells that detonate in a violent matter/antimatter bloom.',
        descriptionEs: 'Proyectiles de aniquilación que detonan en floración materia/antimateria.',
        visualStyle: 'antimatter', muzzleColor: 0xff99ff, trailStyle: 'plasma',
    },
    singularity_cannon: {
        id: 'singularity_cannon', name: 'Singularity Cannon', nameEs: 'Cañón de Singularidad',
        tier: 5, damage: 280, fireRate: 0.5, range: 580, speed: 380, energyCost: 50,
        ammoType: AmmoType.Special, maxAmmo: 20,
        projectileColor: 0x000000, projectileSize: 1.0,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.DarkMatter, amount: 8 }],
        creditCost: 500000, requiredLevel: 60,
        description: 'Collapses a microsingularity on impact; nearly always fatal.',
        descriptionEs: 'Colapsa una singularidad al impacto; casi siempre letal.',
        visualStyle: 'singularity', muzzleColor: 0x222244, trailStyle: 'void',
    },
    nova_lance: {
        id: 'nova_lance', name: 'Nova Lance', nameEs: 'Lanza Nova',
        tier: 5, damage: 175, fireRate: 1.4, range: 680, speed: 999, energyCost: 28,
        ammoType: AmmoType.Special, maxAmmo: 60,
        projectileColor: 0xffcc44, projectileSize: 0.55,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.StarforgeIngot, amount: 3 }],
        creditCost: 700000, requiredLevel: 68,
        description: 'Stellar-fire lance forged by the Nova Forgers.',
        descriptionEs: 'Lanza de fuego estelar forjada por los Forjadores de Novas.',
        visualStyle: 'nova', muzzleColor: 0xffee99, trailStyle: 'plasma',
    },
    // ─── Tier 6 — Legendary ───
    void_beam: {
        id: 'void_beam', name: 'Void Beam', nameEs: 'Rayo del Vacío',
        tier: 6, damage: 175, fireRate: 2.3, range: 800, speed: 999, energyCost: 30,
        ammoType: AmmoType.Special, maxAmmo: 80,
        projectileColor: 0x440088, projectileSize: 0.6,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.QuantumFragment, amount: 4 }],
        creditCost: 1200000, requiredLevel: 80,
        description: 'Ancient Void Walker ray that drinks light as it travels.',
        descriptionEs: 'Rayo ancestral de los Caminantes del Vacío que devora la luz.',
        visualStyle: 'void', muzzleColor: 0x6622aa, trailStyle: 'void',
    },
    ether_harp: {
        id: 'ether_harp', name: 'Ether Harp', nameEs: 'Arpa del Éter',
        tier: 6, damage: 65, fireRate: 3, range: 620, speed: 999, energyCost: 26,
        ammoType: AmmoType.Special, maxAmmo: 100,
        projectileColor: 0x88ffcc, projectileSize: 0.3,
        category: WeaponCategory.Special,
        craftCost: [
            { resource: ResourceTier.EchoCrystal, amount: 3 },
            { resource: ResourceTier.EtherealEssence, amount: 2 },
        ],
        creditCost: 1600000, requiredLevel: 85, projectileCount: 4,
        description: 'Five harmonic strings lance forward in resonant unison.',
        descriptionEs: 'Cinco cuerdas armónicas se lanzan al unísono resonante.',
        visualStyle: 'ether', muzzleColor: 0xaaffdd, trailStyle: 'sparkle',
    },
    abyssal_maw: {
        id: 'abyssal_maw', name: 'Abyssal Maw', nameEs: 'Fauces Abisales',
        tier: 6, damage: 420, fireRate: 0.6, range: 520, speed: 460, energyCost: 60,
        ammoType: AmmoType.Special, maxAmmo: 15,
        projectileColor: 0x110022, projectileSize: 1.2,
        category: WeaponCategory.Special,
        craftCost: [
            { resource: ResourceTier.AbyssalTear, amount: 2 },
            { resource: ResourceTier.SingularityShard, amount: 2 },
        ],
        creditCost: 2500000, requiredLevel: 95,
        description: 'Devours what it touches. Rare. Dangerous. Forbidden.',
        descriptionEs: 'Devora lo que toca. Rara. Peligrosa. Prohibida.',
        visualStyle: 'void', muzzleColor: 0x221144, trailStyle: 'void',
    },
    // ─── Race-gated rare weapons ─────────────────────────────────────────
    // These never appear in station shops. Find a merchant of the listed
    // race (usually deep-sector) to unlock the sale. Each one has a
    // distinctive feel so the trip out there actually matters.
    sylphari_spore_rifle: {
        id: 'sylphari_spore_rifle', name: 'Spore Rifle', nameEs: 'Rifle de Esporas',
        tier: 3, damage: 56, fireRate: 2.5, range: 300, speed: 380, energyCost: 8,
        ammoType: AmmoType.Kinetic, maxAmmo: 180,
        projectileColor: 0x66ff99, projectileSize: 0.32,
        category: WeaponCategory.Projectile,
        craftCost: [{ resource: ResourceTier.Silicate, amount: 40 }],
        creditCost: 14000, requiredLevel: 18,
        description: 'Bio-engineered by the Sylphari — pods burst on impact in a caustic cloud.',
        descriptionEs: 'Bio-diseñado por los Sylphari — cápsulas que estallan en nube cáustica.',
        visualStyle: 'needler', muzzleColor: 0x77ffaa, trailStyle: 'sparkle',
        rarity: 'rare', merchantRaces: [RaceId.Sylphari],
    },
    tharix_phase_cutter: {
        id: 'tharix_phase_cutter', name: 'Phase Cutter', nameEs: 'Cortador de Fase',
        tier: 4, damage: 80, fireRate: 3, range: 340, speed: 999, energyCost: 10,
        ammoType: AmmoType.Energy, maxAmmo: 200,
        projectileColor: 0xcc66ff, projectileSize: 0.3,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.CrystalShards, amount: 40 }],
        creditCost: 32000, requiredLevel: 24,
        description: 'Tharix stealth-tech — bores through shields like they were not there.',
        descriptionEs: 'Tecnología sigilosa Tharix — atraviesa escudos como si no existieran.',
        visualStyle: 'beam', muzzleColor: 0xcc88ff, trailStyle: 'ion',
        rarity: 'rare', merchantRaces: [RaceId.TharixCabal],
    },
    chronos_temporal_shard: {
        id: 'chronos_temporal_shard', name: 'Temporal Shard', nameEs: 'Esquirla Temporal',
        tier: 5, damage: 130, fireRate: 2.0, range: 380, speed: 700, energyCost: 14,
        ammoType: AmmoType.Special, maxAmmo: 120,
        projectileColor: 0xaaffff, projectileSize: 0.45,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.QuantumFragment, amount: 15 }],
        creditCost: 90000, requiredLevel: 36,
        description: 'Chronos Seers bend local time — each shot arrives an instant before it is fired.',
        descriptionEs: 'Los Chronos Seers doblan el tiempo — cada disparo llega antes de ser disparado.',
        visualStyle: 'chrono', muzzleColor: 0xaaffff, trailStyle: 'warp',
        rarity: 'rare', merchantRaces: [RaceId.ChronosSeers],
    },
    krelg_rail_breaker: {
        id: 'krelg_rail_breaker', name: 'Rail Breaker', nameEs: 'Quebrantarrieles',
        tier: 5, damage: 220, fireRate: 1.1, range: 540, speed: 900, energyCost: 20,
        ammoType: AmmoType.Kinetic, maxAmmo: 60,
        projectileColor: 0xffaa33, projectileSize: 0.6,
        category: WeaponCategory.Projectile,
        craftCost: [{ resource: ResourceTier.TitaniumAlloy, amount: 60 }],
        creditCost: 160000, requiredLevel: 44,
        description: 'Krelg dreadnought-grade railgun scaled down to fit a pilot hull.',
        descriptionEs: 'Railgun de acorazado Krelg reducido para un casco de piloto.',
        visualStyle: 'rail', muzzleColor: 0xffcc44, trailStyle: 'smoke',
        rarity: 'rare', merchantRaces: [RaceId.KrelgHegemony],
    },
    nova_forge_lance: {
        id: 'nova_forge_lance', name: 'Forge Lance', nameEs: 'Lanza de Forja',
        tier: 6, damage: 290, fireRate: 1.3, range: 480, speed: 720, energyCost: 30,
        ammoType: AmmoType.Special, maxAmmo: 80,
        projectileColor: 0xffee55, projectileSize: 0.8,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.QuantumFragment, amount: 25 }],
        creditCost: 420000, requiredLevel: 58,
        description: 'Forged in a Nova plasma crucible. White-hot, slow, deletes anything it kisses.',
        descriptionEs: 'Forjada en crisol de plasma Nova. Blanco-incandescente y letal.',
        visualStyle: 'nova', muzzleColor: 0xffee99, trailStyle: 'plasma',
        rarity: 'legendary', merchantRaces: [RaceId.NovaForgers],
    },
    abyssal_chorus_voidsinger: {
        id: 'abyssal_chorus_voidsinger', name: 'Voidsinger', nameEs: 'Cantavacío',
        tier: 6, damage: 460, fireRate: 0.75, range: 520, speed: 480, energyCost: 40,
        ammoType: AmmoType.Special, maxAmmo: 30,
        projectileColor: 0x9944ff, projectileSize: 1.1,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.AbyssalTear, amount: 3 }],
        creditCost: 900000, requiredLevel: 72,
        description: 'Sung into existence by the Abyssal Chorus. Listen closely — the shot hums back.',
        descriptionEs: 'Entonada por el Coro Abisal. Escucha bien — el disparo canta de vuelta.',
        visualStyle: 'void', muzzleColor: 0xaa55ff, trailStyle: 'void',
        rarity: 'legendary', merchantRaces: [RaceId.AbyssalChorus],
    },
    // ─── Common additions (purchasable / craftable) ──────────────────────
    pulse_repeater: {
        id: 'pulse_repeater', name: 'Pulse Repeater', nameEs: 'Repetidor de Pulso',
        tier: 1, damage: 8, fireRate: 9, range: 230, speed: 420, energyCost: 1.2,
        ammoType: AmmoType.Energy, maxAmmo: 400,
        projectileColor: 0xff6644, projectileSize: 0.16,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.ScrapMetal, amount: 25 }],
        creditCost: 900, requiredLevel: 6,
        description: 'Auto-firing pulse blaster — chips shields with steady fire.',
        descriptionEs: 'Bláster automático de pulso — desgasta escudos con fuego sostenido.',
        visualStyle: 'blaster', muzzleColor: 0xff8866, trailStyle: 'basic',
    },
    flak_burster: {
        id: 'flak_burster', name: 'Flak Burster', nameEs: 'Reventador de Flak',
        tier: 2, damage: 18, fireRate: 2.2, range: 280, speed: 460, energyCost: 4,
        ammoType: AmmoType.Kinetic, maxAmmo: 240,
        projectileColor: 0xffaa44, projectileSize: 0.3,
        category: WeaponCategory.Projectile,
        craftCost: [
            { resource: ResourceTier.IronOre, amount: 30 },
            { resource: ResourceTier.CopperOre, amount: 10 },
        ],
        creditCost: 5800, requiredLevel: 13, projectileCount: 4,
        description: 'Air-burst flak shells — four pellets fan out at mid range.',
        descriptionEs: 'Proyectiles de flak — cuatro fragmentos en abanico a media distancia.',
        visualStyle: 'shotgun', muzzleColor: 0xffcc77, trailStyle: 'smoke',
    },
    harpoon_launcher: {
        id: 'harpoon_launcher', name: 'Harpoon Launcher', nameEs: 'Lanza Arpones',
        tier: 3, damage: 150, fireRate: 1.0, range: 480, speed: 440, energyCost: 14,
        ammoType: AmmoType.Missile, maxAmmo: 30,
        projectileColor: 0xddaa66, projectileSize: 0.55,
        category: WeaponCategory.Missile,
        craftCost: [
            { resource: ResourceTier.TitaniumAlloy, amount: 12 },
            { resource: ResourceTier.IronOre, amount: 30 },
        ],
        creditCost: 26000, requiredLevel: 26,
        description: 'Heavy magnetic harpoons — slow, but punch holes through armor.',
        descriptionEs: 'Arpones magnéticos — lentos, pero perforan blindajes pesados.',
        visualStyle: 'torpedo', muzzleColor: 0xffcc99, trailStyle: 'smoke',
    },
    graviton_cannon: {
        id: 'graviton_cannon', name: 'Graviton Cannon', nameEs: 'Cañón de Gravitones',
        tier: 5, damage: 220, fireRate: 0.8, range: 620, speed: 560, energyCost: 32,
        ammoType: AmmoType.Special, maxAmmo: 35,
        projectileColor: 0x6666ff, projectileSize: 0.85,
        category: WeaponCategory.Special,
        craftCost: [
            { resource: ResourceTier.NeutroniumDust, amount: 12 },
            { resource: ResourceTier.PlasmaCore, amount: 8 },
        ],
        creditCost: 380000, requiredLevel: 65,
        description: 'Compressed graviton bolt — bends nearby projectiles into the impact site.',
        descriptionEs: 'Proyectil de gravitones — atrae los disparos cercanos al impacto.',
        visualStyle: 'singularity', muzzleColor: 0x9999ff, trailStyle: 'warp',
    },
    // ─── Race-gated additions ────────────────────────────────────────────
    terran_mass_driver: {
        id: 'terran_mass_driver', name: 'Mass Driver', nameEs: 'Acelerador de Masa',
        tier: 4, damage: 165, fireRate: 1.3, range: 580, speed: 880, energyCost: 18,
        ammoType: AmmoType.Kinetic, maxAmmo: 90,
        projectileColor: 0xddeeff, projectileSize: 0.4,
        category: WeaponCategory.Projectile,
        craftCost: [
            { resource: ResourceTier.IronOre, amount: 60 },
            { resource: ResourceTier.TitaniumAlloy, amount: 20 },
        ],
        creditCost: 70000, requiredLevel: 38,
        description: 'Terran heavy-industry slug thrower — utilitarian and brutal.',
        descriptionEs: 'Cañón cinético de industria pesada Terrana — utilitario y brutal.',
        visualStyle: 'rail', muzzleColor: 0xeeffff, trailStyle: 'sparkle',
        rarity: 'rare', merchantRaces: [RaceId.Terran],
    },
    zyrkath_acid_spitter: {
        id: 'zyrkath_acid_spitter', name: 'Acid Spitter', nameEs: 'Escupe-Ácido',
        tier: 3, damage: 42, fireRate: 4, range: 260, speed: 380, energyCost: 5,
        ammoType: AmmoType.Kinetic, maxAmmo: 240,
        projectileColor: 0x66ff44, projectileSize: 0.28,
        category: WeaponCategory.Projectile,
        craftCost: [{ resource: ResourceTier.Bioflux, amount: 20 }],
        creditCost: 16000, requiredLevel: 20,
        description: "Zyr'kath bio-cannon — gobs of caustic acid that eat plating between volleys.",
        descriptionEs: "Bio-cañón Zyr'kath — escupe ácido cáustico que corroe el blindaje entre andanadas.",
        visualStyle: 'needler', muzzleColor: 0x88ff66, trailStyle: 'sparkle',
        rarity: 'rare', merchantRaces: [RaceId.Zyrkath],
    },
    luminara_prism_lance: {
        id: 'luminara_prism_lance', name: 'Prism Lance', nameEs: 'Lanza Prismática',
        tier: 4, damage: 100, fireRate: 2.8, range: 420, speed: 999, energyCost: 12,
        ammoType: AmmoType.Energy, maxAmmo: 200,
        projectileColor: 0xaaeeff, projectileSize: 0.32,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.CrystalShards, amount: 50 }],
        creditCost: 48000, requiredLevel: 34,
        description: 'Luminara faceted beam — refracts off shielding for surprising spread.',
        descriptionEs: 'Haz Luminara facetado — se refracta en escudos con dispersión inesperada.',
        visualStyle: 'beam', muzzleColor: 0xccf0ff, trailStyle: 'ion',
        rarity: 'rare', merchantRaces: [RaceId.Luminara],
    },
    vokren_hidden_turret: {
        id: 'vokren_hidden_turret', name: 'Hidden Turret', nameEs: 'Torreta Oculta',
        tier: 3, damage: 24, fireRate: 7, range: 280, speed: 480, energyCost: 2,
        ammoType: AmmoType.Kinetic, maxAmmo: 360,
        projectileColor: 0xffcc66, projectileSize: 0.18,
        category: WeaponCategory.Projectile,
        craftCost: [
            { resource: ResourceTier.CopperOre, amount: 30 },
            { resource: ResourceTier.Silicate, amount: 20 },
        ],
        creditCost: 22000, requiredLevel: 22,
        description: "Vok'ren concealed-turret kit — fires bursts as soon as the panel pops.",
        descriptionEs: "Torreta oculta Vok'ren — dispara ráfagas al desplegarse.",
        visualStyle: 'gatling', muzzleColor: 0xffddaa, trailStyle: 'smoke',
        rarity: 'rare', merchantRaces: [RaceId.Vokren],
    },
    obsidian_logic_disruptor: {
        id: 'obsidian_logic_disruptor', name: 'Logic Disruptor', nameEs: 'Disruptor Lógico',
        tier: 5, damage: 110, fireRate: 3.5, range: 480, speed: 820, energyCost: 14,
        ammoType: AmmoType.Energy, maxAmmo: 180,
        projectileColor: 0x4466ff, projectileSize: 0.3,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.NeutroniumDust, amount: 12 }],
        creditCost: 140000, requiredLevel: 50,
        description: 'Obsidian Collective bolts — surgical strikes targeted by distributed AI.',
        descriptionEs: 'Proyectiles del Colectivo Obsidiana — golpes quirúrgicos de IA distribuida.',
        visualStyle: 'disruptor', muzzleColor: 0x6688ff, trailStyle: 'ion',
        rarity: 'rare', merchantRaces: [RaceId.ObsidianCollective],
    },
    voidwalker_shadow_lance: {
        id: 'voidwalker_shadow_lance', name: 'Shadow Lance', nameEs: 'Lanza Sombría',
        tier: 5, damage: 200, fireRate: 1.5, range: 560, speed: 999, energyCost: 22,
        ammoType: AmmoType.Special, maxAmmo: 90,
        projectileColor: 0x4400aa, projectileSize: 0.5,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.VoidEssence, amount: 16 }],
        creditCost: 220000, requiredLevel: 54,
        description: 'Void Walker phase-lance — slips between dimensions before it strikes.',
        descriptionEs: 'Lanza de fase de los Caminantes — se desliza entre dimensiones antes de impactar.',
        visualStyle: 'void', muzzleColor: 0x8822dd, trailStyle: 'void',
        rarity: 'rare', merchantRaces: [RaceId.VoidWalker],
    },
    // ─── Tier 7 — Apex (level 100+) ────────────────────────────────────
    // Power-curve note: from tier 7 on, weapons stop chasing single-shot
    // damage and start adding *qualitative* edges (pierce-shields, target
    // multiple ships, ignore range-falloff). PvP balance is preserved
    // because every tier-7+ option has a meaningful trade-off (slow rate,
    // hot energy bill, narrow ammo pool, or short range).
    paradox_lance: {
        id: 'paradox_lance', name: 'Paradox Lance', nameEs: 'Lanza Paradoja',
        tier: 7, damage: 380, fireRate: 0.9, range: 800, speed: 999, energyCost: 70,
        ammoType: AmmoType.Special, maxAmmo: 50,
        projectileColor: 0xc8d8ff, projectileSize: 0.8,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.QuantumFragment, amount: 20 }],
        creditCost: 4_500_000, requiredLevel: 100,
        description: 'Eidolon paradox-lance — fires two beams that arrive in opposite directions of the same instant.',
        descriptionEs: 'Lanza paradoja Eidolón — dispara dos haces que llegan en direcciones opuestas del mismo instante.',
        visualStyle: 'chrono', muzzleColor: 0xddeeff, trailStyle: 'warp',
        rarity: 'legendary', merchantRaces: [RaceId.EidolonConclave],
    },
    ghost_repeater: {
        id: 'ghost_repeater', name: 'Ghost Repeater', nameEs: 'Repetidor Fantasma',
        tier: 7, damage: 75, fireRate: 9, range: 600, speed: 999, energyCost: 22,
        ammoType: AmmoType.Energy, maxAmmo: 320,
        projectileColor: 0xaab8ff, projectileSize: 0.25,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.PhasePrism, amount: 18 }],
        creditCost: 3_200_000, requiredLevel: 110,
        description: 'Eidolon honour-blade scaled into an automatic. Each round phases through shields outright.',
        descriptionEs: 'Espada de honor Eidolón en automática. Cada disparo atraviesa los escudos de raíz.',
        visualStyle: 'beam', muzzleColor: 0xddeeff, trailStyle: 'ion',
        rarity: 'rare', merchantRaces: [RaceId.EidolonConclave],
    },
    hymn_cannon: {
        id: 'hymn_cannon', name: 'Hymn Cannon', nameEs: 'Cañón Himno',
        tier: 7, damage: 480, fireRate: 0.8, range: 720, speed: 560, energyCost: 60,
        ammoType: AmmoType.Special, maxAmmo: 40,
        projectileColor: 0xffd47a, projectileSize: 1.0,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.EtherealEssence, amount: 8 }],
        creditCost: 5_500_000, requiredLevel: 130,
        description: 'Astralleviathan hymn-burst — a city-sized note compressed into a single explosive shell.',
        descriptionEs: 'Estallido-himno Leviatán Astral — una nota del tamaño de una ciudad comprimida en un proyectil.',
        visualStyle: 'plasma', muzzleColor: 0xffe9a8, trailStyle: 'plasma',
        rarity: 'legendary', merchantRaces: [RaceId.AstralLeviathan],
    },
    // ─── Tier 8 — Singularity (level 200+) ─────────────────────────────
    collapse_torch: {
        id: 'collapse_torch', name: 'Collapse Torch', nameEs: 'Antorcha de Colapso',
        tier: 8, damage: 900, fireRate: 0.5, range: 620, speed: 420, energyCost: 90,
        ammoType: AmmoType.Special, maxAmmo: 12,
        projectileColor: 0xff2244, projectileSize: 1.4,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.SingularityShard, amount: 6 }],
        creditCost: 12_000_000, requiredLevel: 200,
        description: 'Sovereign torch loaded with a captive collapse-star. One shot, three deaths.',
        descriptionEs: 'Antorcha Soberana cargada con una estrella-colapso cautiva. Un disparo, tres muertes.',
        visualStyle: 'singularity', muzzleColor: 0xff5566, trailStyle: 'void',
        rarity: 'legendary', merchantRaces: [RaceId.SingularitySovereign],
    },
    starlace_array: {
        id: 'starlace_array', name: 'Starlace Array', nameEs: 'Matriz Cordón Estelar',
        tier: 8, damage: 95, fireRate: 14, range: 540, speed: 700, energyCost: 18,
        ammoType: AmmoType.Energy, maxAmmo: 600,
        projectileColor: 0xffee99, projectileSize: 0.2,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.StarforgeIngot, amount: 12 }],
        creditCost: 9_500_000, requiredLevel: 220, projectileCount: 5,
        description: 'Star-lace array fires a five-string burst that knits incoming hits with a fading needlework of light.',
        descriptionEs: 'Matriz de cordón estelar con ráfaga de cinco hilos que tejen los impactos en un bordado de luz.',
        visualStyle: 'gatling', muzzleColor: 0xffee99, trailStyle: 'sparkle',
        rarity: 'rare', merchantRaces: [RaceId.NovaForgers],
    },
    ether_loom: {
        id: 'ether_loom', name: 'Ether Loom', nameEs: 'Telar del Éter',
        tier: 8, damage: 220, fireRate: 3.5, range: 700, speed: 999, energyCost: 32,
        ammoType: AmmoType.Special, maxAmmo: 200,
        projectileColor: 0xaaffee, projectileSize: 0.4,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.EtherealEssence, amount: 10 }],
        creditCost: 16_000_000, requiredLevel: 260, projectileCount: 3,
        description: 'Triple ethereal threads that weave into a single hit — the more shots land, the harder the next pulses.',
        descriptionEs: 'Tres hilos etéreos que se entrelazan en un solo impacto — cuantos más aciertan, más fuerte pulsa el siguiente.',
        visualStyle: 'ether', muzzleColor: 0xccffee, trailStyle: 'ion',
        rarity: 'legendary', merchantRaces: [RaceId.AbyssalChorus, RaceId.AstralLeviathan],
    },
    // ─── Tier 9 — Transcendent (level 400+) ────────────────────────────
    black_hole_caster: {
        id: 'black_hole_caster', name: 'Black-Hole Caster', nameEs: 'Lanzador de Agujero Negro',
        tier: 9, damage: 1500, fireRate: 0.2, range: 500, speed: 380, energyCost: 200,
        ammoType: AmmoType.Special, maxAmmo: 6,
        projectileColor: 0x000000, projectileSize: 1.8,
        category: WeaponCategory.Special,
        craftCost: [
            { resource: ResourceTier.SingularityShard, amount: 12 },
            { resource: ResourceTier.AbyssalTear, amount: 6 },
        ],
        creditCost: 50_000_000, requiredLevel: 400,
        description: 'Hurls a tame singularity. Six shots per voyage. Most pilots only need one.',
        descriptionEs: 'Lanza una singularidad domesticada. Seis disparos por viaje. La mayoría solo necesita uno.',
        visualStyle: 'singularity', muzzleColor: 0x442266, trailStyle: 'void',
        rarity: 'legendary', merchantRaces: [RaceId.SingularitySovereign],
    },
    aurora_choir: {
        id: 'aurora_choir', name: 'Aurora Choir', nameEs: 'Coro Aurora',
        tier: 9, damage: 320, fireRate: 5, range: 800, speed: 999, energyCost: 45,
        ammoType: AmmoType.Special, maxAmmo: 240,
        projectileColor: 0x88ffcc, projectileSize: 0.5,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.EtherealEssence, amount: 14 }],
        creditCost: 38_000_000, requiredLevel: 450, projectileCount: 6,
        description: 'Six-voice aurora choir — every projectile is a note, and the chord is fatal.',
        descriptionEs: 'Coro aurora de seis voces — cada proyectil es una nota y el acorde es letal.',
        visualStyle: 'ether', muzzleColor: 0xaaffdd, trailStyle: 'sparkle',
        rarity: 'legendary', merchantRaces: [RaceId.AbyssalChorus, RaceId.AstralLeviathan],
    },
    reality_breaker: {
        id: 'reality_breaker', name: 'Reality Breaker', nameEs: 'Quebrarealidad',
        tier: 9, damage: 900, fireRate: 1.0, range: 900, speed: 999, energyCost: 80,
        ammoType: AmmoType.Special, maxAmmo: 80,
        projectileColor: 0xff66ff, projectileSize: 0.7,
        category: WeaponCategory.Special,
        craftCost: [{ resource: ResourceTier.QuantumFragment, amount: 30 }],
        creditCost: 64_000_000, requiredLevel: 550,
        description: 'Tears a hole in causality where the projectile passes. Anything in the line is unmade.',
        descriptionEs: 'Abre un agujero en la causalidad por donde pasa el disparo. Cualquier cosa en la línea queda deshecha.',
        visualStyle: 'rail', muzzleColor: 0xffaaff, trailStyle: 'warp',
        rarity: 'legendary', merchantRaces: [RaceId.SingularitySovereign, RaceId.EidolonConclave],
    },
    // ─── Tier 10 — Beyond (level 700+) ─────────────────────────────────
    infinity_lance: {
        id: 'infinity_lance', name: 'Infinity Lance', nameEs: 'Lanza Infinita',
        tier: 10, damage: 4000, fireRate: 0.15, range: 1200, speed: 999, energyCost: 350,
        ammoType: AmmoType.Special, maxAmmo: 4,
        projectileColor: 0xfff7c0, projectileSize: 2.4,
        category: WeaponCategory.Special,
        craftCost: [
            { resource: ResourceTier.EtherealEssence, amount: 25 },
            { resource: ResourceTier.SingularityShard, amount: 25 },
        ],
        creditCost: 200_000_000, requiredLevel: 750,
        description: 'A single beam fired across the longest possible diagonal of the visible universe.',
        descriptionEs: 'Un único haz disparado por la diagonal más larga del universo visible.',
        visualStyle: 'rail', muzzleColor: 0xffffe0, trailStyle: 'plasma',
        rarity: 'legendary', merchantRaces: [
            RaceId.AbyssalChorus, RaceId.AstralLeviathan,
            RaceId.EidolonConclave, RaceId.SingularitySovereign,
        ],
    },
    cosmic_decree: {
        id: 'cosmic_decree', name: 'Cosmic Decree', nameEs: 'Decreto Cósmico',
        tier: 10, damage: 2200, fireRate: 0.5, range: 1000, speed: 999, energyCost: 200,
        ammoType: AmmoType.Special, maxAmmo: 20,
        projectileColor: 0xff44aa, projectileSize: 1.6,
        category: WeaponCategory.Special,
        craftCost: [
            { resource: ResourceTier.AbyssalTear, amount: 12 },
            { resource: ResourceTier.EtherealEssence, amount: 12 },
        ],
        creditCost: 320_000_000, requiredLevel: 900,
        description: 'A signed declaration of removal. The target ceases to exist before the beam visibly arrives.',
        descriptionEs: 'Una declaración firmada de remoción. El objetivo deja de existir antes de que el haz llegue visiblemente.',
        visualStyle: 'plasma', muzzleColor: 0xff88cc, trailStyle: 'warp',
        rarity: 'legendary', merchantRaces: [RaceId.SingularitySovereign],
    },
    // ─── Premium-only sidegrades ─────────────────────────────────────────
    //
    // PvP-parity rule (AGENTS.md §5): the numeric profile of every
    // premium-only weapon below MUST match a comparable free-tier
    // counterpart at the same level. The "premium edge" is delivered
    // via `pveDamageMul` and `lootBonusMul` — both server-side gated
    // to NPC / asteroid / salvage targets, so head-to-head PvP DPS
    // stays identical between free and premium pilots.
    //
    // Pairing reference (free → premium):
    //   mining_laser     (T1, lvl 2,  14×7   = 98 DPS)  → premium_prospector_beam (lvl 4,  +60% PvE dmg, +25% loot)
    //   plasma_cannon    (T2, lvl 10, 28×3   = 84 DPS)  → premium_salvage_pulser (lvl 12, +50% PvE dmg, +35% loot)
    premium_prospector_beam: {
        id: 'premium_prospector_beam',
        name: 'Prospector Beam', nameEs: 'Haz Prospector',
        // Same DPS profile as mining_laser, just shifted up by 2 levels
        // so the premium rower is meaningfully early-mid game without
        // out-shadowing the free entry-level mining sidearm.
        tier: 1, damage: 14, fireRate: 7, range: 260, speed: 999, energyCost: 2,
        ammoType: AmmoType.Energy, maxAmmo: 250,
        projectileColor: 0xffcc44, projectileSize: 0.28,
        category: WeaponCategory.Energy,
        craftCost: [
            { resource: ResourceTier.ScrapMetal, amount: 30 },
            { resource: ResourceTier.Silicate, amount: 12 },
        ],
        creditCost: 1500, requiredLevel: 4,
        description: 'Premium-only mining beam tuned for PvE — same hit power as a Mining ' +
            'Laser in PvP, but +60% damage to asteroids / NPCs and +25% credit ' +
            'drops on PvE kills.',
        descriptionEs: 'Haz minero solo Premium — mismo daño en PvP que el Láser Minero, pero ' +
            '+60% de daño contra asteroides / NPC y +25% de créditos al matar PvE.',
        visualStyle: 'laser', muzzleColor: 0xffe088, trailStyle: 'sparkle',
        rarity: 'rare',
        premiumOnly: true,
        pveDamageMul: 1.60,
        lootBonusMul: 0.25,
    },
    premium_salvage_pulser: {
        id: 'premium_salvage_pulser',
        name: 'Salvage Pulser', nameEs: 'Pulsador de Salvamento',
        // Mirrors plasma_cannon: T2, dmg 28, fireRate 3, range 320.
        // Premium edge: +50% PvE damage, +35% loot bonus on NPC kills.
        tier: 2, damage: 28, fireRate: 3, range: 320, speed: 520, energyCost: 8,
        ammoType: AmmoType.Energy, maxAmmo: 200,
        projectileColor: 0xffaa44, projectileSize: 0.5,
        category: WeaponCategory.Energy,
        craftCost: [
            { resource: ResourceTier.CrystalShards, amount: 30 },
            { resource: ResourceTier.PlasmaCore, amount: 6 },
        ],
        creditCost: 5500, requiredLevel: 12,
        description: 'Premium-only salvage pulser — equal to a Plasma Cannon in PvP, but ' +
            '+50% damage against asteroids / NPCs and +35% credit drops on PvE kills.',
        descriptionEs: 'Pulsador de salvamento solo Premium — igual al Cañón de Plasma en PvP, ' +
            'pero +50% de daño contra asteroides / NPC y +35% de créditos en PvE.',
        visualStyle: 'plasma', muzzleColor: 0xffcc88, trailStyle: 'plasma',
        rarity: 'rare',
        premiumOnly: true,
        pveDamageMul: 1.50,
        lootBonusMul: 0.35,
    },
    // Pairing reference (free → premium), mid-game batch:
    //   railgun          (T3, lvl 25, 140×0.85 = 119 DPS) → premium_vein_ripper  (lvl 27, +55% PvE dmg, +30% loot)
    //   disruptor_array  (T4, lvl 40, 44×4    = 176 DPS) → premium_reaper_array (lvl 42, +50% PvE dmg, +40% loot)
    premium_vein_ripper: {
        id: 'premium_vein_ripper',
        name: 'Vein Ripper', nameEs: 'Desgarravetas',
        // Mirrors railgun: T3, dmg 140, fireRate 0.85, range 720, speed 1100.
        tier: 3, damage: 140, fireRate: 0.85, range: 720, speed: 1100, energyCost: 20,
        ammoType: AmmoType.Kinetic, maxAmmo: 60,
        projectileColor: 0xffd277, projectileSize: 0.32,
        category: WeaponCategory.Projectile,
        craftCost: [
            { resource: ResourceTier.PlasmaCore, amount: 14 },
            { resource: ResourceTier.NeutroniumDust, amount: 7 },
        ],
        creditCost: 19000, requiredLevel: 27,
        description: 'Premium-only rail slug cut for deep-rock work — identical to a Railgun ' +
            'in PvP, but +55% damage against asteroids / NPCs and +30% credit drops ' +
            'on PvE kills.',
        descriptionEs: 'Proyectil de riel solo Premium para roca profunda — idéntico al Cañón de ' +
            'Riel en PvP, pero +55% de daño contra asteroides / NPC y +30% de créditos ' +
            'al matar PvE.',
        visualStyle: 'rail', muzzleColor: 0xffe6aa, trailStyle: 'sparkle',
        rarity: 'rare',
        premiumOnly: true,
        pveDamageMul: 1.55,
        lootBonusMul: 0.30,
    },
    premium_reaper_array: {
        id: 'premium_reaper_array',
        name: 'Reaper Array', nameEs: 'Matriz Segadora',
        // Mirrors disruptor_array: T4, dmg 44, fireRate 4, range 440, speed 620.
        tier: 4, damage: 44, fireRate: 4, range: 440, speed: 620, energyCost: 12,
        ammoType: AmmoType.Energy, maxAmmo: 160,
        projectileColor: 0xffb84d, projectileSize: 0.4,
        category: WeaponCategory.Energy,
        craftCost: [{ resource: ResourceTier.VoidEssence, amount: 9 }],
        creditCost: 70000, requiredLevel: 42,
        description: 'Premium-only harvest array — equal to a Disruptor Array in PvP, but ' +
            '+50% damage against asteroids / NPCs and +40% credit drops on PvE kills.',
        descriptionEs: 'Matriz de cosecha solo Premium — igual a la Matriz Disruptora en PvP, ' +
            'pero +50% de daño contra asteroides / NPC y +40% de créditos en PvE.',
        visualStyle: 'disruptor', muzzleColor: 0xffd28a, trailStyle: 'ion',
        rarity: 'rare',
        premiumOnly: true,
        pveDamageMul: 1.50,
        lootBonusMul: 0.40,
    },
};
/**
 * Ship shields. Basic energy shield is the default. Advanced shields grant
 * higher capacity, regen behaviour, damage-type resistance, or a chance to
 * dodge incoming shots outright. Every non-basic shield has a finite
 * `maxCharges` — when it eats that many damage hits it breaks and the ship
 * falls back to the basic shield automatically.
 */
export const SHIELDS = {
    basic_energy_shield: {
        id: 'basic_energy_shield', name: 'Basic Energy Shield', nameEs: 'Escudo de Energía Básico',
        tier: 0, maxShield: 50, regenRate: 2, regenDelay: 3000,
        resistances: { kinetic: 0, energy: 0, explosive: 0 }, dodgeChance: 0,
        craftCost: [], creditCost: 0, requiredLevel: 1,
        description: 'Standard-issue energy bubble — nothing special, never breaks.',
        descriptionEs: 'Burbuja energética estándar — nada especial, nunca se rompe.',
        visualColor: 0x44aaff, visualStyle: 'energy', maxCharges: -1,
    },
    reinforced_plating: {
        id: 'reinforced_plating', name: 'Reinforced Plating', nameEs: 'Blindaje Reforzado',
        tier: 1, maxShield: 120, regenRate: 0, regenDelay: 0,
        resistances: { kinetic: 0.3, energy: -0.1, explosive: 0.2 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.ScrapMetal, amount: 40 }],
        creditCost: 3000, requiredLevel: 5,
        description: 'Bolt-on armor that soaks kinetic damage; weak to energy.',
        descriptionEs: 'Blindaje fijo que absorbe daño cinético; débil contra energía.',
        visualColor: 0x999999, visualStyle: 'plating', maxCharges: 60,
    },
    ablative_coating: {
        id: 'ablative_coating', name: 'Ablative Coating', nameEs: 'Recubrimiento Ablativo',
        tier: 1, maxShield: 90, regenRate: 0, regenDelay: 0,
        resistances: { kinetic: 0.1, energy: 0.15, explosive: 0.45 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.IronOre, amount: 30 }],
        creditCost: 3500, requiredLevel: 6,
        description: 'Sacrificial ceramic layer that flakes off under explosive hits.',
        descriptionEs: 'Capa cerámica de sacrificio que se desprende ante explosiones.',
        visualColor: 0xddbb66, visualStyle: 'ablative', maxCharges: 80,
    },
    solar_deflector: {
        id: 'solar_deflector', name: 'Solar Deflector', nameEs: 'Deflector Solar',
        tier: 1, maxShield: 80, regenRate: 4, regenDelay: 2500,
        resistances: { kinetic: 0.05, energy: 0.25, explosive: 0.05 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.SolarPlasma, amount: 10 }],
        creditCost: 4000, requiredLevel: 8,
        description: 'Solar-fed field that resists beam weapons surprisingly well.',
        descriptionEs: 'Campo solar que resiste bien a las armas de haz.',
        visualColor: 0xffcc55, visualStyle: 'energy', maxCharges: 120,
    },
    crystal_barrier: {
        id: 'crystal_barrier', name: 'Crystal Barrier', nameEs: 'Barrera de Cristal',
        tier: 2, maxShield: 140, regenRate: 5, regenDelay: 2000,
        resistances: { kinetic: 0.1, energy: 0.3, explosive: 0.1 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.CrystalShards, amount: 30 }],
        creditCost: 10000, requiredLevel: 15,
        description: 'Facetted crystal lattice that sings when struck by energy weapons.',
        descriptionEs: 'Red cristalina facetada que canta al ser golpeada por energía.',
        visualColor: 0x66ffff, visualStyle: 'crystal', maxCharges: 150,
    },
    bioweave_mesh: {
        id: 'bioweave_mesh', name: 'Bioweave Mesh', nameEs: 'Malla Bioforjada',
        tier: 2, maxShield: 130, regenRate: 7, regenDelay: 1500,
        resistances: { kinetic: 0.15, energy: 0.15, explosive: 0.1 }, dodgeChance: 0.05,
        craftCost: [{ resource: ResourceTier.Bioflux, amount: 18 }],
        creditCost: 12000, requiredLevel: 17,
        description: 'Self-knitting Sylphari mycelium that regrows between fights.',
        descriptionEs: 'Micelio Sylphari autorregenerativo que se teje entre combates.',
        visualColor: 0x88ffbb, visualStyle: 'bioweave', maxCharges: 180,
    },
    phase_shield: {
        id: 'phase_shield', name: 'Phase Shield', nameEs: 'Escudo de Fase',
        tier: 3, maxShield: 110, regenRate: 3, regenDelay: 1500,
        resistances: { kinetic: 0.1, energy: 0.1, explosive: 0.1 }, dodgeChance: 0.18,
        craftCost: [{ resource: ResourceTier.PhasePrism, amount: 6 }],
        creditCost: 30000, requiredLevel: 30,
        description: 'Phases ship partially out of reality — lots of misses, low soak.',
        descriptionEs: 'Desfasa la nave parcialmente — muchos fallos, poca absorción.',
        visualColor: 0xcc66ff, visualStyle: 'phase', maxCharges: 140,
    },
    chrono_aegis: {
        id: 'chrono_aegis', name: 'Chrono Aegis', nameEs: 'Égida Crónica',
        tier: 3, maxShield: 180, regenRate: 6, regenDelay: 1200,
        resistances: { kinetic: 0.2, energy: 0.2, explosive: 0.15 }, dodgeChance: 0.08,
        craftCost: [{ resource: ResourceTier.ChronoQuartz, amount: 6 }],
        creditCost: 45000, requiredLevel: 35,
        description: 'Time-looped aegis that rewinds tiny fractions of incoming damage.',
        descriptionEs: 'Égida en bucle temporal que retrocede fracciones del daño.',
        visualColor: 0xbbeeff, visualStyle: 'chrono', maxCharges: 200,
    },
    prism_field: {
        id: 'prism_field', name: 'Prism Field', nameEs: 'Campo Prismático',
        tier: 3, maxShield: 160, regenRate: 8, regenDelay: 1500,
        resistances: { kinetic: 0.15, energy: 0.35, explosive: 0.05 }, dodgeChance: 0,
        craftCost: [
            { resource: ResourceTier.PhasePrism, amount: 4 },
            { resource: ResourceTier.CrystalShards, amount: 20 },
        ],
        creditCost: 55000, requiredLevel: 38,
        description: 'Refractive shell that bends beams harmlessly around the hull.',
        descriptionEs: 'Cáscara refractiva que desvía haces alrededor del casco.',
        visualColor: 0xffaaff, visualStyle: 'prism', maxCharges: 160,
    },
    void_barrier: {
        id: 'void_barrier', name: 'Void Barrier', nameEs: 'Barrera del Vacío',
        tier: 4, maxShield: 320, regenRate: 10, regenDelay: 1000,
        resistances: { kinetic: 0.25, energy: 0.25, explosive: 0.25 }, dodgeChance: 0.1,
        craftCost: [{ resource: ResourceTier.VoidEssence, amount: 12 }],
        creditCost: 180000, requiredLevel: 50,
        description: 'Void-forged bulwark; excellent general-purpose protection.',
        descriptionEs: 'Bastión forjado del vacío; excelente protección general.',
        visualColor: 0x442288, visualStyle: 'void', maxCharges: 220,
    },
    singularity_skin: {
        id: 'singularity_skin', name: 'Singularity Skin', nameEs: 'Piel de Singularidad',
        tier: 4, maxShield: 260, regenRate: 4, regenDelay: 1800,
        resistances: { kinetic: 0.35, energy: 0.25, explosive: 0.3 }, dodgeChance: 0.12,
        craftCost: [{ resource: ResourceTier.SingularityShard, amount: 3 }],
        creditCost: 250000, requiredLevel: 55,
        description: 'Warped-space skin that redirects impacts into miniature black holes.',
        descriptionEs: 'Piel de espacio curvado que canaliza impactos a agujeros negros.',
        visualColor: 0x110033, visualStyle: 'void', maxCharges: 180,
    },
    nova_corona: {
        id: 'nova_corona', name: 'Nova Corona', nameEs: 'Corona Nova',
        tier: 5, maxShield: 380, regenRate: 12, regenDelay: 900,
        resistances: { kinetic: 0.2, energy: 0.4, explosive: 0.2 }, dodgeChance: 0.05,
        craftCost: [{ resource: ResourceTier.StarforgeIngot, amount: 2 }],
        creditCost: 400000, requiredLevel: 62,
        description: 'Star-hot corona that vaporises incoming energy fire.',
        descriptionEs: 'Corona estelar que vaporiza el fuego energético entrante.',
        visualColor: 0xffaa22, visualStyle: 'nova', maxCharges: 240,
    },
    echo_screen: {
        id: 'echo_screen', name: 'Echo Screen', nameEs: 'Pantalla de Eco',
        tier: 5, maxShield: 300, regenRate: 15, regenDelay: 700,
        resistances: { kinetic: 0.3, energy: 0.3, explosive: 0.3 }, dodgeChance: 0.15,
        craftCost: [{ resource: ResourceTier.EchoCrystal, amount: 3 }],
        creditCost: 550000, requiredLevel: 68,
        description: 'Screen laced with phantom echoes that confuse target locks.',
        descriptionEs: 'Pantalla con ecos fantasma que confunde los bloqueos.',
        visualColor: 0xccddff, visualStyle: 'phase', maxCharges: 260,
    },
    abyssal_ward: {
        id: 'abyssal_ward', name: 'Abyssal Ward', nameEs: 'Guarda Abisal',
        tier: 6, maxShield: 500, regenRate: 18, regenDelay: 600,
        resistances: { kinetic: 0.35, energy: 0.35, explosive: 0.35 }, dodgeChance: 0.12,
        craftCost: [
            { resource: ResourceTier.AbyssalTear, amount: 2 },
            { resource: ResourceTier.VoidPearl, amount: 2 },
        ],
        creditCost: 1400000, requiredLevel: 82,
        description: 'Leviathan-grade ward that hums with deep-void resonance.',
        descriptionEs: 'Guarda de leviatán que resuena con el vacío profundo.',
        visualColor: 0x223355, visualStyle: 'aegis', maxCharges: 300,
    },
    quantum_bulwark: {
        id: 'quantum_bulwark', name: 'Quantum Bulwark', nameEs: 'Baluarte Cuántico',
        tier: 6, maxShield: 420, regenRate: 20, regenDelay: 500,
        resistances: { kinetic: 0.3, energy: 0.3, explosive: 0.3 }, dodgeChance: 0.25,
        craftCost: [{ resource: ResourceTier.QuantumFragment, amount: 3 }],
        creditCost: 2200000, requiredLevel: 90,
        description: 'Probability-warping bulwark — attacks frequently miss entirely.',
        descriptionEs: 'Baluarte que altera probabilidades — los ataques fallan a menudo.',
        visualColor: 0xccffff, visualStyle: 'quantum', maxCharges: 280,
    },
    ether_veil: {
        id: 'ether_veil', name: 'Ether Veil', nameEs: 'Velo del Éter',
        tier: 6, maxShield: 600, regenRate: 25, regenDelay: 400,
        resistances: { kinetic: 0.4, energy: 0.4, explosive: 0.4 }, dodgeChance: 0.2,
        craftCost: [
            { resource: ResourceTier.EtherealEssence, amount: 3 },
            { resource: ResourceTier.QuantumFragment, amount: 2 },
        ],
        creditCost: 3200000, requiredLevel: 98,
        description: 'Legendary veil woven from ethereal essence itself.',
        descriptionEs: 'Velo legendario tejido con esencia etérea pura.',
        visualColor: 0xaaffee, visualStyle: 'quantum', maxCharges: 400,
    },
    // ─── Race-gated rare shields ─────────────────────────────────────────
    sylphari_bioweave: {
        id: 'sylphari_bioweave', name: 'Bioweave Shield', nameEs: 'Escudo Biotejido',
        tier: 3, maxShield: 200, regenRate: 12, regenDelay: 1200,
        resistances: { kinetic: 0.15, energy: 0.05, explosive: 0.2 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.Silicate, amount: 40 }],
        creditCost: 18000, requiredLevel: 18,
        description: 'Living Sylphari weave that regrows itself between fights.',
        descriptionEs: 'Tejido Sylphari vivo que se regenera entre combates.',
        visualColor: 0x88ffaa, visualStyle: 'bioweave', maxCharges: 200,
        rarity: 'rare', merchantRaces: [RaceId.Sylphari],
    },
    tharix_cloak_shield: {
        id: 'tharix_cloak_shield', name: 'Cloak Field', nameEs: 'Campo de Ocultación',
        tier: 4, maxShield: 260, regenRate: 14, regenDelay: 1000,
        resistances: { kinetic: 0.1, energy: 0.25, explosive: 0.1 }, dodgeChance: 0.25,
        craftCost: [{ resource: ResourceTier.CrystalShards, amount: 25 }],
        creditCost: 55000, requiredLevel: 30,
        description: 'Phased cloaking veil — a quarter of incoming fire passes through harmlessly.',
        descriptionEs: 'Velo de fase — un cuarto del fuego enemigo atraviesa sin daño.',
        visualColor: 0xbb66ff, visualStyle: 'phase', maxCharges: 240,
        rarity: 'rare', merchantRaces: [RaceId.TharixCabal],
    },
    abyssal_null_barrier: {
        id: 'abyssal_null_barrier', name: 'Null Barrier', nameEs: 'Barrera Nula',
        tier: 6, maxShield: 780, regenRate: 30, regenDelay: 500,
        resistances: { kinetic: 0.4, energy: 0.45, explosive: 0.4 }, dodgeChance: 0.1,
        craftCost: [{ resource: ResourceTier.AbyssalTear, amount: 4 }],
        creditCost: 1800000, requiredLevel: 78,
        description: 'Abyssal Chorus barrier that simply refuses to let matter through.',
        descriptionEs: 'Barrera del Coro Abisal que simplemente no deja pasar materia.',
        visualColor: 0x220044, visualStyle: 'void', maxCharges: 360,
        rarity: 'legendary', merchantRaces: [RaceId.AbyssalChorus],
    },
    // ─── Common shield additions ─────────────────────────────────────────
    kinetic_buffer: {
        id: 'kinetic_buffer', name: 'Kinetic Buffer', nameEs: 'Amortiguador Cinético',
        tier: 1, maxShield: 75, regenRate: 3, regenDelay: 2200,
        resistances: { kinetic: 0.25, energy: 0, explosive: 0.05 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.IronOre, amount: 25 }],
        creditCost: 2200, requiredLevel: 4,
        description: 'Spring-loaded buffer plate — flexes under kinetic hits.',
        descriptionEs: 'Placa amortiguadora — absorbe impactos cinéticos.',
        visualColor: 0xaaaaee, visualStyle: 'plating', maxCharges: 100,
    },
    static_field: {
        id: 'static_field', name: 'Static Field', nameEs: 'Campo Estático',
        tier: 2, maxShield: 110, regenRate: 6, regenDelay: 1700,
        resistances: { kinetic: 0.05, energy: 0.2, explosive: 0.1 }, dodgeChance: 0.04,
        craftCost: [{ resource: ResourceTier.PlasmaCore, amount: 5 }],
        creditCost: 9000, requiredLevel: 13,
        description: 'Crackling static envelope — shrugs off energy weapons.',
        descriptionEs: 'Envoltura estática crepitante — repele armas de energía.',
        visualColor: 0xaaffff, visualStyle: 'energy', maxCharges: 130,
    },
    aegis_array: {
        id: 'aegis_array', name: 'Aegis Array', nameEs: 'Matriz Égida',
        tier: 3, maxShield: 200, regenRate: 8, regenDelay: 1300,
        resistances: { kinetic: 0.2, energy: 0.2, explosive: 0.2 }, dodgeChance: 0.05,
        craftCost: [
            { resource: ResourceTier.TitaniumAlloy, amount: 25 },
            { resource: ResourceTier.PlasmaCore, amount: 8 },
        ],
        creditCost: 38000, requiredLevel: 28,
        description: 'Multi-layer aegis — solid all-round numbers, no surprises.',
        descriptionEs: 'Égida multicapa — números sólidos, sin sorpresas.',
        visualColor: 0xddddff, visualStyle: 'aegis', maxCharges: 200,
    },
    composite_armor: {
        id: 'composite_armor', name: 'Composite Armor', nameEs: 'Blindaje Compuesto',
        tier: 3, maxShield: 240, regenRate: 0, regenDelay: 0,
        resistances: { kinetic: 0.4, energy: 0.15, explosive: 0.3 }, dodgeChance: 0,
        craftCost: [
            { resource: ResourceTier.TitaniumAlloy, amount: 30 },
            { resource: ResourceTier.Silicate, amount: 25 },
        ],
        creditCost: 42000, requiredLevel: 30,
        description: 'Layered composite plating — no regen, but laughs at slugs.',
        descriptionEs: 'Blindaje compuesto en capas — sin regeneración, ríe ante proyectiles.',
        visualColor: 0xbbaa88, visualStyle: 'plating', maxCharges: 220,
    },
    ablative_lattice: {
        id: 'ablative_lattice', name: 'Ablative Lattice', nameEs: 'Retícula Ablativa',
        tier: 4, maxShield: 300, regenRate: 4, regenDelay: 1700,
        resistances: { kinetic: 0.2, energy: 0.2, explosive: 0.5 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.NeutroniumDust, amount: 10 }],
        creditCost: 130000, requiredLevel: 46,
        description: 'Honey-comb ablative lattice — eats explosive damage for breakfast.',
        descriptionEs: 'Retícula ablativa en panal — devora daño explosivo.',
        visualColor: 0xeebb66, visualStyle: 'ablative', maxCharges: 200,
    },
    // ─── Race-gated shield additions ─────────────────────────────────────
    terran_modular_plating: {
        id: 'terran_modular_plating', name: 'Modular Plating', nameEs: 'Blindaje Modular',
        tier: 3, maxShield: 220, regenRate: 6, regenDelay: 1500,
        resistances: { kinetic: 0.3, energy: 0.15, explosive: 0.2 }, dodgeChance: 0,
        craftCost: [
            { resource: ResourceTier.IronOre, amount: 50 },
            { resource: ResourceTier.TitaniumAlloy, amount: 12 },
        ],
        creditCost: 25000, requiredLevel: 22,
        description: 'Bolt-on Terran plating — easy to repair in the field.',
        descriptionEs: 'Blindaje Terrano modular — fácil de reparar en campaña.',
        visualColor: 0x88aacc, visualStyle: 'plating', maxCharges: 220,
        rarity: 'rare', merchantRaces: [RaceId.Terran],
    },
    vokren_ironclad_shield: {
        id: 'vokren_ironclad_shield', name: 'Ironclad Shield', nameEs: 'Escudo Acorazado',
        tier: 4, maxShield: 360, regenRate: 3, regenDelay: 1800,
        resistances: { kinetic: 0.45, energy: 0.1, explosive: 0.35 }, dodgeChance: 0,
        craftCost: [{ resource: ResourceTier.TitaniumAlloy, amount: 40 }],
        creditCost: 95000, requiredLevel: 42,
        description: "Vok'ren ironclad — heavy slabs of reinforced plating bolted on.",
        descriptionEs: "Acorazado Vok'ren — pesadas planchas reforzadas atornilladas al casco.",
        visualColor: 0xddaa66, visualStyle: 'plating', maxCharges: 280,
        rarity: 'rare', merchantRaces: [RaceId.Vokren],
    },
    sylphari_blossom_field: {
        id: 'sylphari_blossom_field', name: 'Blossom Field', nameEs: 'Campo Florecer',
        tier: 4, maxShield: 280, regenRate: 18, regenDelay: 800,
        resistances: { kinetic: 0.1, energy: 0.2, explosive: 0.15 }, dodgeChance: 0.05,
        craftCost: [{ resource: ResourceTier.Bioflux, amount: 25 }],
        creditCost: 110000, requiredLevel: 44,
        description: 'Sylphari pollen-cloud — fast-regenerating petals trap incoming fire.',
        descriptionEs: 'Polen Sylphari — pétalos de regeneración rápida atrapan el fuego entrante.',
        visualColor: 0xffaadd, visualStyle: 'bioweave', maxCharges: 240,
        rarity: 'rare', merchantRaces: [RaceId.Sylphari],
    },
    obsidian_nano_aegis: {
        id: 'obsidian_nano_aegis', name: 'Nano Aegis', nameEs: 'Égida Nano',
        tier: 5, maxShield: 340, regenRate: 16, regenDelay: 800,
        resistances: { kinetic: 0.25, energy: 0.3, explosive: 0.25 }, dodgeChance: 0.1,
        craftCost: [{ resource: ResourceTier.NeutroniumDust, amount: 18 }],
        creditCost: 320000, requiredLevel: 56,
        description: 'Obsidian nano-swarm — distributed micro-shields self-repair on the fly.',
        descriptionEs: 'Enjambre nano del Colectivo — micro-escudos distribuidos que se autorreparan.',
        visualColor: 0x6688ff, visualStyle: 'quantum', maxCharges: 260,
        rarity: 'rare', merchantRaces: [RaceId.ObsidianCollective],
    },
    krelg_warhammer_aegis: {
        id: 'krelg_warhammer_aegis', name: 'Warhammer Aegis', nameEs: 'Égida Martillo de Guerra',
        tier: 5, maxShield: 460, regenRate: 5, regenDelay: 1500,
        resistances: { kinetic: 0.5, energy: 0.15, explosive: 0.4 }, dodgeChance: 0,
        craftCost: [
            { resource: ResourceTier.TitaniumAlloy, amount: 45 },
            { resource: ResourceTier.NeutroniumDust, amount: 10 },
        ],
        creditCost: 360000, requiredLevel: 60,
        description: 'Krelg dreadnought-grade aegis — eats slugs, laughs, asks for more.',
        descriptionEs: 'Égida de acorazado Krelg — devora proyectiles, ríe y pide más.',
        visualColor: 0xff8844, visualStyle: 'aegis', maxCharges: 320,
        rarity: 'rare', merchantRaces: [RaceId.KrelgHegemony],
    },
    // ─── Tier 7+ shields (level 100+) ────────────────────────────────────
    // Apex shields trade huge shield pools for either heavy weight (low
    // dodge / regen) or expensive crafting. Resistances move slowly into
    // 0.5+ territory so they actually feel different from tier-6 walls.
    mirror_aegis: {
        id: 'mirror_aegis', name: 'Mirror Aegis', nameEs: 'Égida Espejo',
        tier: 7, maxShield: 800, regenRate: 24, regenDelay: 500,
        resistances: { kinetic: 0.45, energy: 0.5, explosive: 0.4 }, dodgeChance: 0.18,
        craftCost: [{ resource: ResourceTier.PhasePrism, amount: 18 }],
        creditCost: 6_000_000, requiredLevel: 110,
        description: 'Eidolon mirror-aegis — bounces a fraction of every shot back along its incoming line.',
        descriptionEs: 'Égida espejo Eidolón — devuelve parte de cada disparo por su línea entrante.',
        visualColor: 0xc0c8d8, visualStyle: 'aegis', maxCharges: 360,
        rarity: 'rare', merchantRaces: [RaceId.EidolonConclave],
    },
    leviathan_skin: {
        id: 'leviathan_skin', name: 'Leviathan Skin', nameEs: 'Piel de Leviatán',
        tier: 7, maxShield: 1100, regenRate: 14, regenDelay: 800,
        resistances: { kinetic: 0.5, energy: 0.4, explosive: 0.45 }, dodgeChance: 0.05,
        craftCost: [{ resource: ResourceTier.AbyssalTear, amount: 5 }],
        creditCost: 7_500_000, requiredLevel: 140,
        description: 'Astralleviathan flank-plate, scaled down. Slow regen, but it absorbs an absurd amount before breaking.',
        descriptionEs: 'Plancha de costado Leviatán Astral, a escala. Regenera lento pero absorbe muchísimo antes de romperse.',
        visualColor: 0x150a3a, visualStyle: 'plating', maxCharges: 500,
        rarity: 'legendary', merchantRaces: [RaceId.AstralLeviathan],
    },
    collapse_lattice: {
        id: 'collapse_lattice', name: 'Collapse Lattice', nameEs: 'Retícula de Colapso',
        tier: 8, maxShield: 1400, regenRate: 30, regenDelay: 600,
        resistances: { kinetic: 0.5, energy: 0.5, explosive: 0.5 }, dodgeChance: 0.20,
        craftCost: [{ resource: ResourceTier.SingularityShard, amount: 10 }],
        creditCost: 18_000_000, requiredLevel: 220,
        description: 'Sovereign collapse-lattice — bends space tight enough that hits arrive somewhere else.',
        descriptionEs: 'Retícula de colapso Soberano — pliega el espacio tanto que los impactos llegan en otro sitio.',
        visualColor: 0x000000, visualStyle: 'void', maxCharges: 600,
        rarity: 'legendary', merchantRaces: [RaceId.SingularitySovereign],
    },
    starforge_rampart: {
        id: 'starforge_rampart', name: 'Starforge Rampart', nameEs: 'Bastión Forja Estelar',
        tier: 8, maxShield: 1700, regenRate: 18, regenDelay: 700,
        resistances: { kinetic: 0.6, energy: 0.4, explosive: 0.55 }, dodgeChance: 0.05,
        craftCost: [{ resource: ResourceTier.StarforgeIngot, amount: 14 }],
        creditCost: 22_000_000, requiredLevel: 260,
        description: 'Stellar-forged rampart, hammered out at the heart of a captive star.',
        descriptionEs: 'Bastión forjado estelar, martillado en el corazón de una estrella cautiva.',
        visualColor: 0xffaa22, visualStyle: 'plating', maxCharges: 800,
        rarity: 'legendary', merchantRaces: [RaceId.NovaForgers],
    },
    aurora_veil: {
        id: 'aurora_veil', name: 'Aurora Veil', nameEs: 'Velo Aurora',
        tier: 9, maxShield: 2200, regenRate: 40, regenDelay: 400,
        resistances: { kinetic: 0.55, energy: 0.6, explosive: 0.5 }, dodgeChance: 0.30,
        craftCost: [
            { resource: ResourceTier.EtherealEssence, amount: 16 },
            { resource: ResourceTier.QuantumFragment, amount: 16 },
        ],
        creditCost: 60_000_000, requiredLevel: 480,
        description: 'Tier-9 aurora veil — reality declines a third of all incoming damage on principle.',
        descriptionEs: 'Velo aurora de tier 9 — la realidad rechaza un tercio del daño entrante por principio.',
        visualColor: 0xaaffee, visualStyle: 'quantum', maxCharges: 1000,
        rarity: 'legendary', merchantRaces: [RaceId.AbyssalChorus, RaceId.AstralLeviathan],
    },
    infinity_aegis: {
        id: 'infinity_aegis', name: 'Infinity Aegis', nameEs: 'Égida Infinita',
        tier: 10, maxShield: 4000, regenRate: 60, regenDelay: 300,
        resistances: { kinetic: 0.65, energy: 0.65, explosive: 0.6 }, dodgeChance: 0.35,
        craftCost: [
            { resource: ResourceTier.EtherealEssence, amount: 30 },
            { resource: ResourceTier.SingularityShard, amount: 20 },
            { resource: ResourceTier.AbyssalTear, amount: 15 },
        ],
        creditCost: 250_000_000, requiredLevel: 800,
        description: 'Tier-10 aegis. Cannot be lost in honest combat. Saved fleets have ended wars over one of these.',
        descriptionEs: 'Égida de tier 10. No puede perderse en combate justo. Flotas enteras han terminado guerras por una de estas.',
        visualColor: 0xfff7c0, visualStyle: 'quantum', maxCharges: 1500,
        rarity: 'legendary', merchantRaces: [
            RaceId.AbyssalChorus, RaceId.AstralLeviathan,
            RaceId.EidolonConclave, RaceId.SingularitySovereign,
        ],
    },
    // ─── Premium-only sidegrade shield ───────────────────────────────────
    //
    // Pairing: matches `crystal_barrier` (T2, lvl 15, 140 / 5 / 2000,
    // resistances 0.10/0.30/0.10) for PvP-equivalent profile. Premium
    // edge: server-side `pveDamageReduction = 0.20` shaves 20% off
    // every NPC / asteroid / mining-drone hit BEFORE the resistance
    // ladder runs. PvP hits are unaffected.
    premium_aegis_barrier: {
        id: 'premium_aegis_barrier',
        name: 'Aegis Barrier', nameEs: 'Barrera Égida',
        tier: 2, maxShield: 140, regenRate: 5, regenDelay: 2000,
        resistances: { kinetic: 0.10, energy: 0.30, explosive: 0.10 }, dodgeChance: 0,
        craftCost: [
            { resource: ResourceTier.CrystalShards, amount: 30 },
            { resource: ResourceTier.PlasmaCore, amount: 6 },
        ],
        creditCost: 11000, requiredLevel: 15,
        description: 'Premium-only barrier — same combat profile as a Crystal Barrier in ' +
            'PvP, plus 20% damage reduction against asteroids and NPCs. Farms ' +
            'last a lot longer between repair stops.',
        descriptionEs: 'Barrera solo Premium — mismo perfil de combate que la Barrera de Cristal ' +
            'en PvP, más 20% de reducción de daño contra asteroides y NPC. Las sesiones ' +
            'de farmeo aguantan mucho más entre paradas de reparación.',
        visualColor: 0xffeebb, visualStyle: 'aegis', maxCharges: 150,
        rarity: 'rare',
        premiumOnly: true,
        pveDamageReduction: 0.20,
    },
    // Pairing: matches `ablative_lattice` (T4, lvl 46, 300 / 4 / 1700,
    // resistances 0.20/0.20/0.50) so PvP profile is identical. Premium
    // edge: 25% PvE damage reduction — deep-sector farming endurance.
    premium_bulwark_lattice: {
        id: 'premium_bulwark_lattice',
        name: 'Bulwark Lattice', nameEs: 'Retícula Baluarte',
        tier: 4, maxShield: 300, regenRate: 4, regenDelay: 1700,
        resistances: { kinetic: 0.2, energy: 0.2, explosive: 0.5 }, dodgeChance: 0,
        craftCost: [
            { resource: ResourceTier.NeutroniumDust, amount: 9 },
            { resource: ResourceTier.TitaniumAlloy, amount: 20 },
        ],
        creditCost: 135000, requiredLevel: 46,
        description: 'Premium-only lattice — same combat profile as an Ablative Lattice in ' +
            'PvP, plus 25% damage reduction against asteroids and NPCs. Built for ' +
            'long hauls through hostile deep sectors.',
        descriptionEs: 'Retícula solo Premium — mismo perfil de combate que la Retícula Ablativa ' +
            'en PvP, más 25% de reducción de daño contra asteroides y NPC. Hecha para ' +
            'travesías largas por sectores profundos hostiles.',
        visualColor: 0xffe0a0, visualStyle: 'aegis', maxCharges: 200,
        rarity: 'rare',
        premiumOnly: true,
        pveDamageReduction: 0.25,
    },
};
/** Ordered list of weapons for menus (sorted by required level). */
export const WEAPONS_ORDERED = Object.values(WEAPONS).sort((a, b) => a.requiredLevel - b.requiredLevel);
/** Ordered list of shields for menus (sorted by required level). */
export const SHIELDS_ORDERED = Object.values(SHIELDS).sort((a, b) => a.requiredLevel - b.requiredLevel);
export const DEFAULT_WEAPON_ID = 'pulse_blaster';
export const DEFAULT_SHIELD_ID = 'basic_energy_shield';
export const ENGINES = {
    basic_thruster: {
        id: 'basic_thruster', name: 'Basic Thruster', nameEs: 'Propulsor Básico',
        tier: 0, maxSpeed: 100, acceleration: 50, turnRate: 2.0,
        boostMultiplier: 1.5, boostEnergyCost: 10,
        craftCost: [], creditCost: 0, requiredLevel: 1,
        description: 'Civilian-grade thruster. Gets the job done — barely.',
        descriptionEs: 'Propulsor civil básico. Cumple su función — apenas.',
    },
    ion_drive: {
        id: 'ion_drive', name: 'Ion Drive', nameEs: 'Propulsor Iónico',
        tier: 1, maxSpeed: 130, acceleration: 65, turnRate: 2.3,
        boostMultiplier: 1.8, boostEnergyCost: 8,
        craftCost: [{ resource: ResourceTier.ScrapMetal, amount: 25 }],
        creditCost: 3000, requiredLevel: 8,
        description: 'Ionized exhaust raises the cap and trims boost drain.',
        descriptionEs: 'Escape ionizado que eleva el tope y reduce el consumo al impulsar.',
    },
    plasma_drive: {
        id: 'plasma_drive', name: 'Plasma Drive', nameEs: 'Propulsor de Plasma',
        tier: 2, maxSpeed: 170, acceleration: 85, turnRate: 2.8,
        boostMultiplier: 2.0, boostEnergyCost: 6,
        craftCost: [{ resource: ResourceTier.CrystalShards, amount: 20 }],
        creditCost: 12000, requiredLevel: 20,
        description: 'Superheated plasma exhaust — fast, responsive, and hungry.',
        descriptionEs: 'Escape de plasma — rápido, preciso, y muy hambriento.',
    },
    warp_engine: {
        id: 'warp_engine', name: 'Warp Engine', nameEs: 'Motor de Deformación',
        tier: 4, maxSpeed: 250, acceleration: 120, turnRate: 3.5,
        boostMultiplier: 3.0, boostEnergyCost: 4,
        craftCost: [{ resource: ResourceTier.VoidEssence, amount: 10 }],
        creditCost: 80000, requiredLevel: 45,
        description: 'Folds local space ahead of the hull. Civilian law forbids it — of course.',
        descriptionEs: 'Pliega el espacio delante del casco. La ley civil lo prohíbe — claro.',
    },
    // ─── Race-gated rare engines ─────────────────────────────────────────
    // These do NOT appear in station shops. Only merchant NPCs of the
    // matching race sell them (see `buildMerchantStock`). Intentionally tuned
    // as *sidegrades* to warp_engine (comparable top speed) with a distinct
    // flavor, so PvP doesn't become "whoever farmed the Sylphari wins".
    sylphari_bloom_drive: {
        id: 'sylphari_bloom_drive', name: 'Bloom Drive', nameEs: 'Motor Floración',
        tier: 3, maxSpeed: 200, acceleration: 100, turnRate: 4.0,
        boostMultiplier: 2.4, boostEnergyCost: 5,
        craftCost: [], creditCost: 55000, requiredLevel: 30,
        description: 'Grown, not built. Trades top-end speed for razor agility.',
        descriptionEs: 'Cultivado, no fabricado. Cambia velocidad punta por agilidad.',
        rarity: 'rare', merchantRaces: [RaceId.Sylphari],
    },
    chronos_temporal_drive: {
        id: 'chronos_temporal_drive', name: 'Temporal Drive', nameEs: 'Motor Temporal',
        tier: 3, maxSpeed: 180, acceleration: 90, turnRate: 2.8,
        boostMultiplier: 2.5, boostEnergyCost: 2,
        craftCost: [], creditCost: 60000, requiredLevel: 32,
        description: 'Folds the clock around the hull — boost lasts forever, practically.',
        descriptionEs: 'Pliega el tiempo alrededor del casco — el impulso apenas se agota.',
        rarity: 'rare', merchantRaces: [RaceId.ChronosSeers],
    },
    // ─── Common engine additions ─────────────────────────────────────────
    pulse_thruster: {
        id: 'pulse_thruster', name: 'Pulse Thruster', nameEs: 'Propulsor Pulsado',
        tier: 1, maxSpeed: 115, acceleration: 60, turnRate: 2.2,
        boostMultiplier: 1.7, boostEnergyCost: 9,
        craftCost: [{ resource: ResourceTier.IronOre, amount: 18 }],
        creditCost: 1800, requiredLevel: 5,
        description: 'Cheap pulse cycler — cleaner thrust curve than the basic kit.',
        descriptionEs: 'Propulsor pulsado económico — empuje más limpio que el básico.',
    },
    ion_drive_mk2: {
        id: 'ion_drive_mk2', name: 'Ion Drive Mk II', nameEs: 'Propulsor Iónico Mk II',
        tier: 2, maxSpeed: 150, acceleration: 75, turnRate: 2.6,
        boostMultiplier: 1.9, boostEnergyCost: 7,
        craftCost: [
            { resource: ResourceTier.ScrapMetal, amount: 35 },
            { resource: ResourceTier.CrystalShards, amount: 8 },
        ],
        creditCost: 6500, requiredLevel: 14,
        description: 'Refined ion drive — mid-tier civilian darling.',
        descriptionEs: 'Propulsor iónico refinado — favorito civil de gama media.',
    },
    solar_sail: {
        id: 'solar_sail', name: 'Solar Sail', nameEs: 'Vela Solar',
        tier: 3, maxSpeed: 200, acceleration: 80, turnRate: 2.4,
        boostMultiplier: 2.6, boostEnergyCost: 1,
        craftCost: [{ resource: ResourceTier.SolarPlasma, amount: 15 }],
        creditCost: 32000, requiredLevel: 28,
        description: 'Solar-fed sail array — almost free boost, sluggish in shadow.',
        descriptionEs: 'Vela alimentada por sol — impulso casi gratis, lenta a la sombra.',
    },
    quantum_drift: {
        id: 'quantum_drift', name: 'Quantum Drift', nameEs: 'Deriva Cuántica',
        tier: 5, maxSpeed: 280, acceleration: 130, turnRate: 4.2,
        boostMultiplier: 2.8, boostEnergyCost: 5,
        craftCost: [{ resource: ResourceTier.QuantumFragment, amount: 4 }],
        creditCost: 220000, requiredLevel: 60,
        description: 'Quantum drift drive — top-end speed, exotic to maintain.',
        descriptionEs: 'Motor de deriva cuántica — velocidad punta exótica de mantener.',
    },
    // ─── Race-gated engine additions ─────────────────────────────────────
    zyrkath_pulse_drive: {
        id: 'zyrkath_pulse_drive', name: 'Bio-Pulse Drive', nameEs: 'Bio-Propulsor Pulsado',
        tier: 3, maxSpeed: 170, acceleration: 95, turnRate: 3.6,
        boostMultiplier: 2.2, boostEnergyCost: 4,
        craftCost: [{ resource: ResourceTier.Bioflux, amount: 18 }],
        creditCost: 38000, requiredLevel: 24,
        description: "Zyr'kath bio-engine — pulses with rhythmic thrust spikes.",
        descriptionEs: "Bio-motor Zyr'kath — pulsa con picos rítmicos de empuje.",
        rarity: 'rare', merchantRaces: [RaceId.Zyrkath],
    },
    voidwalker_phase_drive: {
        id: 'voidwalker_phase_drive', name: 'Phase Drive', nameEs: 'Motor de Fase',
        tier: 4, maxSpeed: 220, acceleration: 110, turnRate: 3.4,
        boostMultiplier: 2.7, boostEnergyCost: 3,
        craftCost: [{ resource: ResourceTier.VoidEssence, amount: 12 }],
        creditCost: 95000, requiredLevel: 40,
        description: 'Void Walker phase-drive — slips between thrust frames.',
        descriptionEs: 'Motor de fase Caminante — se desliza entre tramos de empuje.',
        rarity: 'rare', merchantRaces: [RaceId.VoidWalker],
    },
    tharix_silent_drive: {
        id: 'tharix_silent_drive', name: 'Silent Drive', nameEs: 'Motor Silente',
        tier: 4, maxSpeed: 195, acceleration: 100, turnRate: 3.8,
        boostMultiplier: 2.4, boostEnergyCost: 4,
        craftCost: [{ resource: ResourceTier.PhasePrism, amount: 5 }],
        creditCost: 85000, requiredLevel: 36,
        description: 'Tharix muffled drive — leaves no thermal trail behind.',
        descriptionEs: 'Motor silenciado Tharix — no deja estela térmica.',
        rarity: 'rare', merchantRaces: [RaceId.TharixCabal],
    },
    krelg_overpressured_drive: {
        id: 'krelg_overpressured_drive', name: 'Overpressured Drive', nameEs: 'Motor Sobrepresurizado',
        tier: 4, maxSpeed: 210, acceleration: 140, turnRate: 2.6,
        boostMultiplier: 3.0, boostEnergyCost: 6,
        craftCost: [{ resource: ResourceTier.TitaniumAlloy, amount: 35 }],
        creditCost: 110000, requiredLevel: 42,
        description: 'Krelg overpressured drive — neck-snapping shove, shaky turns.',
        descriptionEs: 'Motor sobrepresurizado Krelg — empujón brutal, giros temblorosos.',
        rarity: 'rare', merchantRaces: [RaceId.KrelgHegemony],
    },
    nova_starforge_drive: {
        id: 'nova_starforge_drive', name: 'Starforge Drive', nameEs: 'Motor Forja Estelar',
        tier: 5, maxSpeed: 240, acceleration: 130, turnRate: 3.2,
        boostMultiplier: 2.9, boostEnergyCost: 4,
        craftCost: [{ resource: ResourceTier.StarforgeIngot, amount: 2 }],
        creditCost: 280000, requiredLevel: 56,
        description: 'Nova Forger plasma drive — burns hot, runs forever.',
        descriptionEs: 'Motor de plasma Forjador — arde fuerte, no se apaga.',
        rarity: 'rare', merchantRaces: [RaceId.NovaForgers],
    },
    abyssal_resonant_drive: {
        id: 'abyssal_resonant_drive', name: 'Resonant Drive', nameEs: 'Motor Resonante',
        tier: 6, maxSpeed: 260, acceleration: 120, turnRate: 3.5,
        boostMultiplier: 3.1, boostEnergyCost: 3,
        craftCost: [{ resource: ResourceTier.AbyssalTear, amount: 2 }],
        creditCost: 650000, requiredLevel: 75,
        description: 'Abyssal Chorus resonant drive — hums at deep-void harmonics.',
        descriptionEs: 'Motor resonante Coro Abisal — vibra en armónicos del vacío profundo.',
        rarity: 'legendary', merchantRaces: [RaceId.AbyssalChorus],
    },
    // ─── Tier 7+ engines (level 100+) ────────────────────────────────────
    // PvP-parity note: tier-7+ drives all sit in roughly the same speed
    // band (~280 → 340) but vary on turn-rate / boost-cost so the lineup
    // reads as a series of feel-different sidegrades, not a power ladder.
    mirror_drive: {
        id: 'mirror_drive', name: 'Mirror Drive', nameEs: 'Motor Espejo',
        tier: 7, maxSpeed: 290, acceleration: 140, turnRate: 4.6,
        boostMultiplier: 3.0, boostEnergyCost: 3,
        craftCost: [{ resource: ResourceTier.PhasePrism, amount: 12 }],
        creditCost: 4_200_000, requiredLevel: 110,
        description: 'Eidolon mirror-drive — reflections of the hull arrive a tick before the ship does.',
        descriptionEs: 'Motor espejo Eidolón — los reflejos del casco llegan un instante antes que la nave.',
        rarity: 'rare', merchantRaces: [RaceId.EidolonConclave],
    },
    leviathan_glide: {
        id: 'leviathan_glide', name: 'Leviathan Glide', nameEs: 'Deslizador Leviatán',
        tier: 8, maxSpeed: 320, acceleration: 150, turnRate: 3.0,
        boostMultiplier: 3.4, boostEnergyCost: 2,
        craftCost: [{ resource: ResourceTier.AbyssalTear, amount: 6 }],
        creditCost: 11_000_000, requiredLevel: 200,
        description: 'Astralleviathan glide-organ — silent, slow to turn, and capable of crossing entire sectors on a thimble of energy.',
        descriptionEs: 'Órgano-deslizador Leviatán Astral — silencioso, lento de girar, capaz de cruzar sectores enteros con un dedal de energía.',
        rarity: 'legendary', merchantRaces: [RaceId.AstralLeviathan],
    },
    event_horizon_drive: {
        id: 'event_horizon_drive', name: 'Event Horizon Drive', nameEs: 'Motor Horizonte de Sucesos',
        tier: 9, maxSpeed: 360, acceleration: 200, turnRate: 4.8,
        boostMultiplier: 3.8, boostEnergyCost: 4,
        craftCost: [{ resource: ResourceTier.SingularityShard, amount: 8 }],
        creditCost: 28_000_000, requiredLevel: 320,
        description: 'Sovereign drive that pushes off a captive black hole. Acceleration is brutal; coming to a stop is the hard part.',
        descriptionEs: 'Motor Soberano que se impulsa contra un agujero negro cautivo. La aceleración es brutal; lo difícil es detenerse.',
        rarity: 'legendary', merchantRaces: [RaceId.SingularitySovereign],
    },
    ether_drift: {
        id: 'ether_drift', name: 'Ether Drift', nameEs: 'Deriva Etérea',
        tier: 9, maxSpeed: 340, acceleration: 170, turnRate: 5.2,
        boostMultiplier: 3.5, boostEnergyCost: 2,
        craftCost: [{ resource: ResourceTier.EtherealEssence, amount: 8 }],
        creditCost: 32_000_000, requiredLevel: 400,
        description: 'Tier-9 ether-drift — coast through gravity wells like they were not there.',
        descriptionEs: 'Deriva etérea de tier 9 — atraviesa pozos gravitacionales como si no existieran.',
        rarity: 'legendary',
    },
    infinity_drive: {
        id: 'infinity_drive', name: 'Infinity Drive', nameEs: 'Motor Infinito',
        tier: 10, maxSpeed: 420, acceleration: 240, turnRate: 5.5,
        boostMultiplier: 4.2, boostEnergyCost: 3,
        craftCost: [
            { resource: ResourceTier.EtherealEssence, amount: 20 },
            { resource: ResourceTier.SingularityShard, amount: 14 },
        ],
        creditCost: 180_000_000, requiredLevel: 800,
        description: 'Tier-10 drive. Capable of reaching the edge of the playable universe in a single charge cycle.',
        descriptionEs: 'Motor de tier 10. Capaz de alcanzar el borde del universo jugable en un solo ciclo de carga.',
        rarity: 'legendary',
    },
    // ─── Premium-only sidegrade engine ───────────────────────────────────
    //
    // Pairing: identical thrust / max-speed / boost profile to
    // `ion_drive` (T1, lvl 8) so PvP combat speed is unchanged. The
    // premium edge is delivered by `warpDiscountMul = 0.10` (extra
    // 10% off warp gate cost ON TOP of `PREMIUM_PERKS.warpCostMul`)
    // and `lootMagnetMul = 1.5` (+50% pickup radius for ore /
    // salvage / cargo crates). Both are pure economy / PvE perks.
    premium_pathfinder_drive: {
        id: 'premium_pathfinder_drive',
        name: 'Pathfinder Drive', nameEs: 'Motor Explorador',
        tier: 1, maxSpeed: 130, acceleration: 65, turnRate: 2.3,
        boostMultiplier: 1.8, boostEnergyCost: 8,
        craftCost: [
            { resource: ResourceTier.ScrapMetal, amount: 35 },
            { resource: ResourceTier.PlasmaCore, amount: 4 },
        ],
        creditCost: 4500, requiredLevel: 8,
        description: 'Premium-only pathfinder drive — identical thrust to an Ion Drive, plus ' +
            'an extra 10% off WARP gate fees and a 50% wider loot magnet for ore, ' +
            'salvage, and cargo. Built for traders who need to clear sectors fast.',
        descriptionEs: 'Motor explorador solo Premium — mismo empuje que un Propulsor Iónico, ' +
            'más un 10% extra de descuento en portales WARP y un magneto de botín ' +
            '50% más amplio para mineral, chatarra y carga. Hecho para mercaderes que ' +
            'necesitan vaciar sectores rápido.',
        rarity: 'rare',
        premiumOnly: true,
        warpDiscountMul: 0.10,
        lootMagnetMul: 1.50,
    },
    // Pairing: identical thrust / max-speed / boost profile to
    // `warp_engine` (T4, lvl 45) so PvP mobility is unchanged. Premium
    // edge: 15% extra warp-fee discount + 75% wider loot magnet — the
    // mid-game upgrade path from the Pathfinder Drive.
    premium_voyager_drive: {
        id: 'premium_voyager_drive',
        name: 'Voyager Drive', nameEs: 'Motor Viajero',
        tier: 4, maxSpeed: 250, acceleration: 120, turnRate: 3.5,
        boostMultiplier: 3.0, boostEnergyCost: 4,
        craftCost: [
            { resource: ResourceTier.VoidEssence, amount: 9 },
            { resource: ResourceTier.PlasmaCore, amount: 10 },
        ],
        creditCost: 85000, requiredLevel: 45,
        description: 'Premium-only voyager drive — identical thrust to a Warp Engine, plus ' +
            'an extra 15% off WARP gate fees and a 75% wider loot magnet. The ' +
            'long-haul successor to the Pathfinder Drive.',
        descriptionEs: 'Motor viajero solo Premium — mismo empuje que un Motor de Deformación, ' +
            'más un 15% extra de descuento en portales WARP y un magneto de botín ' +
            '75% más amplio. El sucesor de largo alcance del Motor Explorador.',
        rarity: 'rare',
        premiumOnly: true,
        warpDiscountMul: 0.15,
        lootMagnetMul: 1.75,
    },
};
export const ENGINES_ORDERED = Object.values(ENGINES).sort((a, b) => a.requiredLevel - b.requiredLevel);
export const DEFAULT_ENGINE_ID = 'basic_thruster';
/**
 * Baseline values used by the server physics layer to turn a raw engine
 * definition into a percentage multiplier. `basic_thruster` is the
 * reference — its stats must stay at these exact numbers.
 */
export const ENGINE_BASELINE = {
    maxSpeed: 100,
    acceleration: 50,
};
export function lookupEngine(id) {
    return ENGINES[id] ?? null;
}
/**
 * Return the definition for a given item id across all equipment tables.
 * Used by shops and crafting handlers to validate ids submitted by clients.
 */
export function lookupWeapon(id) {
    return WEAPONS[id] ?? null;
}
export function lookupShield(id) {
    return SHIELDS[id] ?? null;
}
//# sourceMappingURL=weapons.js.map